import { Question } from '../types';
import { stage2Questions } from './stage2-questions';
import { stage3Questions } from './stage3-questions';

/**
 * 题库数据
 * 涵盖新生儿期、1-3月、4-6月、7-12月四个阶段所有关卡
 * 每个关卡约 4-7 题，包含单选、多选、判断、情景题
 */
export const questions: Question[] = [
  // ==========================================
  // 喂养模块 - 关卡 1：宝宝是不是饿了？
  // ==========================================
  {
    id: 'q-f1-1',
    levelId: 'newborn-feeding-1',
    type: 'single',
    text: '以下哪一种更可能是新生儿较早的饥饿信号？',
    options: ['持续大哭', '转头寻找、张嘴', '睡得很沉', '完全没有反应'],
    correctAnswer: '转头寻找、张嘴',
    explanation: '转头寻找、张嘴、吸吮动作等通常属于较早的饥饿信号。哭可能已经是较晚信号。',
  },
  {
    id: 'q-f1-2',
    levelId: 'newborn-feeding-1',
    type: 'truefalse',
    text: '只要宝宝没哭，就说明一定不饿。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '宝宝可能在哭之前已经通过身体动作和口部动作表达饥饿。不能只用哭声作为唯一判断标准。',
  },
  {
    id: 'q-f1-3',
    levelId: 'newborn-feeding-1',
    type: 'multiple',
    text: '判断宝宝是否吃得合适，可以参考哪些信息？',
    options: ['宝宝吃奶时的状态', '尿便情况', '体重增长趋势', '家人主观觉得"应该够了"'],
    correctAnswer: ['宝宝吃奶时的状态', '尿便情况', '体重增长趋势'],
    explanation: '喂养判断应结合多个观察指标，不能只依赖主观感觉。',
  },
  {
    id: 'q-f1-4',
    levelId: 'newborn-feeding-1',
    type: 'scenario',
    text: '宝宝出生第10天，距离上次吃奶约2小时，现在开始转头寻找、嘴巴张合、手靠近嘴边，但还没有哭。家人说"不哭就不用喂"。你更合适的做法是？',
    options: ['等宝宝大哭后再喂', '观察这些可能是饥饿信号，可以准备喂养', '立即判断宝宝生病了', '完全不处理'],
    correctAnswer: '观察这些可能是饥饿信号，可以准备喂养',
    explanation: '转头寻找、张嘴、吸吮动作等可能是饥饿信号。此时可以准备喂养，不必一定等到大哭。',
  },

  // ==========================================
  // 喂养模块 - 关卡 1 补充：奶粉冲调基础知识
  // ==========================================
  {
    id: 'q-f1-5',
    levelId: 'newborn-feeding-1',
    type: 'single',
    text: '冲调婴儿配方奶粉时，最适宜的水温是？',
    options: ['直接用沸水冲调', '冷却至约 40-50°C 的温开水（或按奶粉罐标注温度）', '用冷水冲调更方便', '温度无所谓，只要冲开就行'],
    correctAnswer: '冷却至约 40-50°C 的温开水（或按奶粉罐标注温度）',
    explanation: 'WHO 建议用不低于 70°C 的水冲调后冷却至适口温度喂给宝宝，以杀灭奶粉中可能存在的阪崎肠杆菌等致病菌。但日常操作建议按奶粉罐标注的温度（通常 40-50°C），冲好后手腕内侧试温。绝不能直接用沸水以免破坏营养和烫伤宝宝。',
  },
  {
    id: 'q-f1-6',
    levelId: 'newborn-feeding-1',
    type: 'truefalse',
    text: '为了让宝宝长得更好，可以在冲调奶粉时多加一勺奶粉，让奶更浓更有营养。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '奶粉必须严格按照罐身标注的比例冲调（通常 1 平勺配 30ml 水）。奶粉过浓会增加宝宝肾脏负担，可能导致脱水、便秘甚至电解质紊乱；奶粉过稀则营养不足影响生长发育。用罐内自带的量勺，刮平时不要压实。',
  },
  {
    id: 'q-f1-7',
    levelId: 'newborn-feeding-1',
    type: 'scenario',
    text: '宝宝出生第 3 天，家人选择了某品牌的新生儿配方奶粉。冲调时，奶奶说"多放半勺奶粉，宝宝吃了长得好"。爸爸看着奶粉罐说"上面写一平勺配 30ml 水，就按这个来"。你会怎么处理？',
    options: [
      '听奶奶的，多加点奶粉让宝宝长快点',
      '严格按照奶粉罐标注的比例冲调，不随意增减',
      '奶粉量随意，差不多就行',
      '先随便冲，宝宝不吃再调整',
    ],
    correctAnswer: '严格按照奶粉罐标注的比例冲调，不随意增减',
    explanation: '配方奶粉的冲调比例是经过科学计算的，确保渗透压、营养密度和水分比例都在新生儿能承受的安全范围内。随意加量会加重肾脏负担，严重时可导致高钠血症、脱水。每一次冲调都应使用罐内量勺，刮平取一平勺。',
  },

  // ==========================================
  // 喂养模块 - 关卡 2：拍嗝与吐奶
  // ==========================================
  {
    id: 'q-f2-1',
    levelId: 'newborn-feeding-2',
    type: 'single',
    text: '新生儿喂完奶后，拍嗝的最佳时机是？',
    options: ['喂完奶立即拍', '喂完奶后让宝宝平躺5分钟再拍', '喂奶过程中和喂完后都可以拍', '不需要拍嗝'],
    correctAnswer: '喂奶过程中和喂完后都可以拍',
    explanation: '可以在喂奶间隙和喂完后轻拍宝宝背部帮助排出空气，减少吐奶风险。',
  },
  {
    id: 'q-f2-2',
    levelId: 'newborn-feeding-2',
    type: 'truefalse',
    text: '宝宝吐奶就是生病了，需要立刻去医院。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '少量吐奶（溢奶）在新生儿期很常见，可能与胃容量小、贲门松弛有关。但如果吐奶频繁、喷射状、颜色异常或伴有精神差，应及时就医。',
  },
  {
    id: 'q-f2-3',
    levelId: 'newborn-feeding-2',
    type: 'scenario',
    text: '宝宝吃完奶后你拍了嗝，但放下5分钟后还是吐了一口奶，量不大，宝宝精神状态良好。接下来你会怎么做？',
    options: [
      '将宝宝侧身或抱起清理口腔，竖抱片刻观察',
      '让宝宝继续平躺，认为没事',
      '马上带宝宝去医院',
      '减少下一次喂奶量',
    ],
    correctAnswer: '将宝宝侧身或抱起清理口腔，竖抱片刻观察',
    explanation: '少量吐奶后，应清理宝宝口腔周围的奶液防止误吸，然后竖抱片刻利用重力减少再次反流。吐完奶后宝宝精神好则无需拍背（奶已吐出）或就医。注意：这与喂奶中呛奶的处理方式不同——呛奶时不能竖直拍背。',
  },
  {
    id: 'q-f2-4',
    levelId: 'newborn-feeding-2',
    type: 'single',
    text: '以下哪种吐奶情况需要及时就医？',
    options: [
      '喂奶后嘴角溢出少量奶',
      '喷射状呕吐、呕吐物呈黄绿色或带血',
      '拍嗝时带出一小口奶',
      '偶尔吐一小口，精神好',
    ],
    correctAnswer: '喷射状呕吐、呕吐物呈黄绿色或带血',
    explanation: '喷射状呕吐、颜色异常、量多，或伴有精神差、哭闹不安等情况，需要及时就医排查。',
  },

  // ==========================================
  // 喂养模块 - 关卡 2 补充：奶瓶喂养技巧
  // ==========================================
  {
    id: 'q-f2-5',
    levelId: 'newborn-feeding-2',
    type: 'single',
    text: '以下哪一项是正确奶瓶喂养姿势的关键点？',
    options: [
      '让宝宝完全平躺喝奶',
      '让宝宝保持半竖立姿势，奶瓶倾斜使奶嘴充满奶液',
      '把奶瓶竖直倒立让奶流快速进入',
      '宝宝含住奶嘴尖端即可',
    ],
    correctAnswer: '让宝宝保持半竖立姿势，奶瓶倾斜使奶嘴充满奶液',
    explanation: '正确瓶喂姿势：宝宝半竖立（约 45°），奶瓶倾斜确保奶嘴中充满奶液而非空气，可以减少吞入空气引发的胀气和呛奶风险。奶嘴应让宝宝张大嘴含住较宽底部，而非仅含尖端。',
  },
  {
    id: 'q-f2-6',
    levelId: 'newborn-feeding-2',
    type: 'truefalse',
    text: '冲调好奶粉后，最可靠的方法是将奶瓶贴在脸颊上来测试温度是否合适。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '最可靠的方法是滴几滴奶液在手腕内侧——这里的皮肤较薄敏感，能准确感知温度。贴在脸颊上不够准确。奶液应该是微温的（接近体温 37°C），不烫不凉。坚决杜绝用嘴直接吸奶嘴试温，这会引入口腔细菌。',
  },
  {
    id: 'q-f2-7',
    levelId: 'newborn-feeding-2',
    type: 'multiple',
    text: '瓶喂时，以下哪些是宝宝正在有效吃到奶的信号？',
    options: [
      '听到或看到宝宝有规律的吞咽动作',
      '奶瓶中持续有气泡冒出',
      '宝宝嘴角有少量奶液溢出',
      '每次喂奶时间必须超过 1 小时',
    ],
    correctAnswer: ['听到或看到宝宝有规律的吞咽动作', '奶瓶中持续有气泡冒出', '宝宝嘴角有少量奶液溢出'],
    explanation: '有效瓶喂的信号包括：有节奏的吞咽声/动作、奶瓶中有规律的气泡冒出（说明宝宝在吸出奶液）、喂完后宝宝表情满足。嘴角少量溢出是正常的。喂奶时长因人而异，新生儿一般 15-30 分钟完成一次瓶喂，不是越长越好。',
  },

  // ==========================================
  // 喂养模块 - 关卡 3：判断是否吃够
  // ==========================================
  {
    id: 'q-f3-1',
    levelId: 'newborn-feeding-3',
    type: 'multiple',
    text: '判断新生儿是否吃到足够奶量，可以参考以下哪些指标？',
    options: ['每天有足够的湿尿布（约6-8片）', '体重按预期增长', '宝宝吃奶后满足安静', '只要不哭就说明吃够了'],
    correctAnswer: ['每天有足够的湿尿布（约6-8片）', '体重按预期增长', '宝宝吃奶后满足安静'],
    explanation: '尿量、体重增长和宝宝精神状态是判断喂养是否充足的客观指标，不能仅凭单一表现。',
  },
  {
    id: 'q-f3-2',
    levelId: 'newborn-feeding-3',
    type: 'truefalse',
    text: '只要按照固定时间隔2小时喂一次，宝宝就一定吃够了。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '喂养不应只看时间表。宝宝有自己的饥饿节奏，应结合饥饿信号、吸吮表现和排泄情况综合判断。',
  },
  {
    id: 'q-f3-3',
    levelId: 'newborn-feeding-3',
    type: 'scenario',
    text: '宝宝出生第7天，每次吃奶约15分钟后入睡，但隔1小时就醒来又要吃。尿布每天约4-5片。你的评估是？',
    options: [
      '正常现象，新生儿进食频率高',
      '宝宝一定没吃饱，应该加配方奶',
      '宝宝生病了，需要去医院',
      '让宝宝哭一会儿，拉长喂养间隔',
    ],
    correctAnswer: '正常现象，新生儿进食频率高',
    explanation: '新生儿胃容量小，频繁进食是正常的。但需持续关注尿量和体重增长。如果尿量持续偏少，可咨询专业人士。',
  },
  {
    id: 'q-f3-4',
    levelId: 'newborn-feeding-3',
    type: 'single',
    text: '新生儿出生后前几天体重下降多少属于正常范围？',
    options: ['下降不超过出生体重的 7%-10%', '下降 20% 以内', '完全不下降', '下降 15% 正常'],
    correctAnswer: '下降不超过出生体重的 7%-10%',
    explanation: '新生儿出生后会有生理性体重下降，通常在7%-10%以内，一般7-10天后恢复出生体重。但需由医生监测。',
  },

  // ==========================================
  // 睡眠模块 - 关卡 1：安全睡姿
  // ==========================================
  {
    id: 'q-s1-1',
    levelId: 'newborn-sleep-1',
    type: 'single',
    text: '为降低婴儿猝死综合征（SIDS）风险，新生儿应保持什么睡姿？',
    options: ['仰卧', '侧卧', '俯卧', '任意姿势'],
    correctAnswer: '仰卧',
    explanation: '权威指南推荐婴儿始终仰卧入睡，这是降低SIDS风险最重要措施之一。',
  },
  {
    id: 'q-s1-2',
    levelId: 'newborn-sleep-1',
    type: 'multiple',
    text: '新生儿安全的睡眠环境应包括以下哪些？',
    options: [
      '坚硬的床垫、床单紧绷',
      '床上放置毛绒玩具和枕头',
      '与父母同室但不同床',
      '使用厚被子保暖',
    ],
    correctAnswer: ['坚硬的床垫、床单紧绷', '与父母同室但不同床'],
    explanation: '安全睡眠环境应使用坚硬的床垫、无松软物品。AAP推荐同室不同床至少到6个月。避免使用枕头、被子、毛绒玩具等。',
  },
  {
    id: 'q-s1-3',
    levelId: 'newborn-sleep-1',
    type: 'truefalse',
    text: '为了防止宝宝头型变扁，应该让新生儿多侧睡或趴睡。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '虽然需要关注头型，但安全第一。可以在宝宝醒着时多进行俯趴练习（Tummy Time），也变换抱的方向，不建议改变安全睡姿。',
  },
  {
    id: 'q-s1-4',
    levelId: 'newborn-sleep-1',
    type: 'scenario',
    text: '冬天室温较低，长辈觉得宝宝会冷，想给宝宝盖厚被子、加枕头。作为主要照护者，你的做法是？',
    options: [
      '听长辈的，给宝宝加厚被子和枕头',
      '使用睡袋或可穿式毯子替代被子，不放置枕头',
      '让宝宝穿多几件厚衣服即可',
      '开暖气到很高温度即可',
    ],
    correctAnswer: '使用睡袋或可穿式毯子替代被子，不放置枕头',
    explanation: '睡袋/可穿式毯子既能保暖又不会覆盖宝宝面部。婴儿床上不应放置枕头、厚被子等松软物品，以降低窒息风险。',
  },

  // ==========================================
  // 睡眠模块 - 关卡 2：夜醒应对
  // ==========================================
  {
    id: 'q-s2-1',
    levelId: 'newborn-sleep-2',
    type: 'single',
    text: '新生儿夜间频繁醒来，最常见的原因是什么？',
    options: ['一定是生病了', '大脑发育不成熟，睡眠周期短', '一定是饿了', '一定是环境太吵'],
    correctAnswer: '大脑发育不成熟，睡眠周期短',
    explanation: '新生儿睡眠周期短（约50-60分钟），浅睡眠比例高，频繁醒来是正常的发育现象。',
  },
  {
    id: 'q-s2-2',
    levelId: 'newborn-sleep-2',
    type: 'scenario',
    text: '宝宝夜间醒来哭闹，你首先应该排查的顺序是？',
    options: [
      '直接抱起拍哄 → 喂奶 → 检查尿布',
      '观察状态 → 检查尿布/温度 → 判断是否饥饿 → 安抚',
      '立刻喂奶 → 换尿布 → 哄睡',
      '让宝宝哭到再次入睡',
    ],
    correctAnswer: '观察状态 → 检查尿布/温度 → 判断是否饥饿 → 安抚',
    explanation: '夜醒时应先观察宝宝状态，再按逻辑排查原因：尿布是否湿了、冷热是否合适、是否饥饿，再做针对性处理。',
  },
  {
    id: 'q-s2-3',
    levelId: 'newborn-sleep-2',
    type: 'truefalse',
    text: '新生儿夜里哭就应该马上喂奶。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '夜醒不一定都是饿了。可能尿布湿了、太冷或太热、或只是需要安抚。先排查原因，再决定行动。',
  },
  {
    id: 'q-s2-4',
    levelId: 'newborn-sleep-2',
    type: 'multiple',
    text: '以下哪些做法有助于改善新生儿夜醒？',
    options: [
      '建立昼夜区别（白天明亮、夜间昏暗安静）',
      '白天让宝宝一直睡、晚上自然就醒了',
      '睡前建立简单的安抚程序',
      '夜间喂奶换尿布时保持安静、避免过度刺激',
    ],
    correctAnswer: [
      '建立昼夜区别（白天明亮、夜间昏暗安静）',
      '睡前建立简单的安抚程序',
      '夜间喂奶换尿布时保持安静、避免过度刺激',
    ],
    explanation: '帮助宝宝建立昼夜节律需要白天有光照和互动，夜间保持安静昏暗，让宝宝逐渐区分白天和晚上。',
  },

  // ==========================================
  // 睡眠模块 - 关卡 3：昼夜节律
  // ==========================================
  {
    id: 'q-s3-1',
    levelId: 'newborn-sleep-3',
    type: 'single',
    text: '帮助新生儿建立昼夜节律，白天正确的做法是？',
    options: [
      '拉上所有窗帘保持黑暗',
      '保持正常光线、适当活动和互动',
      '让宝宝一直睡不要打扰',
      '让宝宝看电子屏幕',
    ],
    correctAnswer: '保持正常光线、适当活动和互动',
    explanation: '白天应保持正常光线，适当与宝宝互动、玩耍、练习俯趴（Tummy Time），帮助宝宝区分白天和晚上。',
  },
  {
    id: 'q-s3-2',
    levelId: 'newborn-sleep-3',
    type: 'truefalse',
    text: '新生儿出生就具备稳定的昼夜节律，不需要特别帮助。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '新生儿的昼夜节律尚未成熟，需要照护者帮助建立。一般到3-4个月后开始逐渐形成稳定的昼夜节律。',
  },
  {
    id: 'q-s3-3',
    levelId: 'newborn-sleep-3',
    type: 'scenario',
    text: '你准备在晚上给宝宝洗澡后喂奶、然后进行轻柔的抚触、调暗灯光、小声讲故事。家人认为没必要这么麻烦。你怎么解释？',
    options: [
      '不解释，继续做自己的',
      '这是帮助宝宝建立睡前程序，有助于逐渐区分昼夜',
      '同意家人的看法，停止这些行为',
      '让家人完全接管夜间照料',
    ],
    correctAnswer: '这是帮助宝宝建立睡前程序，有助于逐渐区分昼夜',
    explanation: '固定的睡前程序（洗澡→喂奶→抚触→昏暗安静）有助于宝宝逐渐建立昼夜节律，是育儿的科学方法。',
  },

  // ==========================================
  // 喂养模块 - 额外关卡：第一次喂养（补充）
  // ==========================================
  {
    id: 'q-f0-1',
    levelId: 'newborn-feeding-1',
    type: 'truefalse',
    text: '新生儿出生后越早开始喂养越好，最好在出生后 1 小时内开始。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '早期喂养（出生后1小时内）有利于稳定血糖、建立喂养规律。对于奶粉喂养的宝宝，首次喂养量通常为 15-30ml，观察宝宝的接受度和吞咽情况。具体情况需遵医嘱。',
  },

  // ==========================================
  // 日常护理模块 - 关卡 1：脐带护理
  // ==========================================
  {
    id: 'q-c1-1',
    levelId: 'newborn-care-1',
    type: 'single',
    text: '新生儿脐带残端一般多久会自然脱落？',
    options: ['出生后 2-3 天', '出生后 1-3 周', '出生后 1 个月以上', '不需要脱落'],
    correctAnswer: '出生后 1-3 周',
    explanation: '脐带残端在保持清洁干燥的情况下，通常 1-3 周内会自行萎缩脱落。3 周后仍未脱落建议咨询医生。',
  },
  {
    id: 'q-c1-2',
    levelId: 'newborn-care-1',
    type: 'truefalse',
    text: '根据现代指南，应每天用酒精棉签擦拭脐带根部来消毒。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '当前 AAP 等权威指南不推荐常规使用酒精擦拭脐带，保持清洁干燥即可。过度使用酒精反而可能延迟脐带脱落。',
  },
  {
    id: 'q-c1-3',
    levelId: 'newborn-care-1',
    type: 'multiple',
    text: '以下哪些是脐带感染的警示信号？',
    options: ['脐周皮肤明显发红、肿胀', '脐带有少量清澈渗出液', '脐带有黄色脓性、有异味的分泌物', '触碰脐带区域时宝宝哭闹抗拒'],
    correctAnswer: ['脐周皮肤明显发红、肿胀', '脐带有黄色脓性、有异味的分泌物', '触碰脐带区域时宝宝哭闹抗拒'],
    explanation: '感染信号包括红肿、脓性分泌物、触痛等。少量清澈渗出液在脱落过程中是正常的，但伴有红肿需警惕。',
  },
  {
    id: 'q-c1-4',
    levelId: 'newborn-care-1',
    type: 'scenario',
    text: '宝宝出生第 10 天，脐带还没有脱落。今天换尿布时发现脐带根部有少量血迹，脐周皮肤颜色正常，宝宝精神吃奶都好。你该怎么做？',
    options: [
      '立即带宝宝去急诊',
      '继续观察，保持脐部清洁干爽，将尿布边缘折到肚脐以下',
      '用创可贴把肚脐贴住防止出血',
      '用酒精大力擦拭脐带根部',
    ],
    correctAnswer: '继续观察，保持脐部清洁干爽，将尿布边缘折到肚脐以下',
    explanation: '脐带脱落过程中少量出血是正常的。正确做法是保持清洁干燥，尿布不要覆盖脐部。如果出血不止、周围红肿或有脓性分泌物，则应就医。',
  },
  {
    id: 'q-c1-5',
    levelId: 'newborn-care-1',
    type: 'single',
    text: '穿纸尿裤时如何处理脐带部位？',
    options: [
      '把纸尿裤拉到肚脐以上盖住脐带',
      '将纸尿裤上缘折到肚脐以下，让脐带暴露在空气中',
      '用纱布把脐带和纸尿裤一起包住',
      '不需要特别处理',
    ],
    correctAnswer: '将纸尿裤上缘折到肚脐以下，让脐带暴露在空气中',
    explanation: '纸尿裤上缘应折到肚脐以下，避免尿液浸湿脐带残端，同时保持脐带通风干燥，有利于自然脱落。',
  },

  // ==========================================
  // 日常护理模块 - 关卡 2：洗澡
  // ==========================================
  {
    id: 'q-c2-1',
    levelId: 'newborn-care-2',
    type: 'single',
    text: '新生儿脐带尚未脱落时，正确的清洗方式是？',
    options: ['将宝宝放入浴盆洗澡', '用湿毛巾做擦浴', '用淋浴喷头冲洗', '暂时什么都不能洗'],
    correctAnswer: '用湿毛巾做擦浴',
    explanation: '脐带未脱落/未愈合前应做擦浴（用湿毛巾分部位擦洗），避免脐带浸泡在水中。脐带脱落后、肚脐完全愈合方可盆浴。',
  },
  {
    id: 'q-c2-2',
    levelId: 'newborn-care-2',
    type: 'truefalse',
    text: '给宝宝洗澡时，把热水器的最高温度调到 49℃ 是一个安全措施。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: 'AAP 建议将家用热水器最高温度设为 49℃，以防意外烫伤。洗澡水温应以手腕或手肘内侧感觉温热（约37-38℃）为宜。',
  },
  {
    id: 'q-c2-3',
    levelId: 'newborn-care-2',
    type: 'scenario',
    text: '你在给宝宝盆浴时发现忘了拿浴巾，浴巾在隔壁房间。你会怎么做？',
    options: [
      '快速跑去隔壁拿浴巾，宝宝在水里很安全',
      '把宝宝从水里抱起来，用毛巾简单包好一起带去拿',
      '大声叫家人来帮忙，自己守在浴盆旁',
      '让宝宝在水里等着，自己不走开',
    ],
    correctAnswer: '把宝宝从水里抱起来，用毛巾简单包好一起带去拿',
    explanation: '任何情况下都不能将宝宝独自留在水中，哪怕只有几秒钟。需要离开时必须抱着宝宝一起走。如果有人在旁边可以帮忙，也可以请人拿。',
  },
  {
    id: 'q-c2-4',
    levelId: 'newborn-care-2',
    type: 'multiple',
    text: '从水龙头接洗澡水时，正确的操作是？',
    options: [
      '先放冷水，后放热水',
      '关水时先关热水，后关冷水',
      '先放热水再加冷水调节',
      '用手腕或手肘内侧试水温',
    ],
    correctAnswer: ['先放冷水，后放热水', '关水时先关热水，后关冷水', '用手腕或手肘内侧试水温'],
    explanation: '正确顺序是先冷后热，关闭时先热后冷，防止烫伤。手腕/手肘内侧是最合适的试温部位。水深约 5 厘米即可。',
  },
  {
    id: 'q-c2-5',
    levelId: 'newborn-care-2',
    type: 'truefalse',
    text: '1 岁以下的婴儿应该每天都用沐浴液洗澡。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '对于 1 岁以下婴儿，每周用低刺激性沐浴液洗 3 次即可。过于频繁洗澡并使用沐浴产品反而会使皮肤干燥、破坏皮肤屏障。',
  },

  // ==========================================
  // 日常护理模块 - 关卡 3：换尿布
  // ==========================================
  {
    id: 'q-c3-1',
    levelId: 'newborn-care-3',
    type: 'single',
    text: '新生儿尿布疹最主要的原因是什么？',
    options: [
      '纸尿裤品牌不好',
      '皮肤长时间接触尿液和粪便',
      '宝宝对牛奶蛋白过敏',
      '天气太热',
    ],
    correctAnswer: '皮肤长时间接触尿液和粪便',
    explanation: '尿液分解后产生化学物质刺激皮肤，大便中的消化酶会侵蚀皮肤。长时间未更换尿布是尿布疹最主要的原因。',
  },
  {
    id: 'q-c3-2',
    levelId: 'newborn-care-3',
    type: 'multiple',
    text: '预防新生儿尿布疹，正确的做法包括？',
    options: [
      '每次大便后立即更换尿布并清洁臀部',
      '经常更换尿湿的尿布，减少皮肤与尿液接触时间',
      '换尿布时有机会就让孩子臀部暴露在空气中晾一会儿',
      '每次换尿布都涂厚厚一层爽身粉',
    ],
    correctAnswer: ['每次大便后立即更换尿布并清洁臀部', '经常更换尿湿的尿布，减少皮肤与尿液接触时间', '换尿布时有机会就让孩子臀部暴露在空气中晾一会儿'],
    explanation: '预防三原则：勤换、清洁干爽、适当通风。爽身粉可能被宝宝吸入肺部，且对尿布疹没有治疗作用。应使用含氧化锌的护臀膏。',
  },
  {
    id: 'q-c3-3',
    levelId: 'newborn-care-3',
    type: 'truefalse',
    text: '出生后第 1 周尿布上有粉色或红褐色痕迹，一定是尿血，需要马上去医院。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '出生后第1周尿布上的粉色痕迹通常是尿液高度浓缩所致（尿酸盐结晶），只要每天有足够数量的湿尿布，一般不用担心。但如果持续存在或伴有其他症状，应咨询医生。',
  },
  {
    id: 'q-c3-4',
    levelId: 'newborn-care-3',
    type: 'scenario',
    text: '宝宝 2 个月大，最近几天臀部和大腿内侧出现了一片红疹，有些小疙瘩。宝宝精神吃奶正常。你应该怎么做？',
    options: [
      '保持臀部的清洁干爽，每次换尿布后涂含氧化锌的护臀膏，观察 2-3 天',
      '马上停止使用纸尿裤，全部换成布尿布',
      '涂抗生素药膏',
      '不用处理，等它自己好',
    ],
    correctAnswer: '保持臀部的清洁干爽，每次换尿布后涂含氧化锌的护臀膏，观察 2-3 天',
    explanation: '轻度尿布疹应勤换尿布、清洁干爽、使用护臀膏保护。通常 2-3 天会明显改善。如果涂护臀膏后加重或出现脓疱，需就医排除酵母菌感染。',
  },
  {
    id: 'q-c3-5',
    levelId: 'newborn-care-3',
    type: 'single',
    text: '健康新生儿每天一般尿湿多少片尿布算正常？',
    options: ['1-2 片', '4-6 片以上', '10 片以上', '1 片就够了'],
    correctAnswer: '4-6 片以上',
    explanation: '健康新生儿通常每天会尿湿 4-6 片以上的尿布，约每 1-3 小时排尿一次。尿布数量结合尿液颜色是判断水分摄入是否充足的重要指标。',
  },

  // ==========================================
  // 安全防护模块 - 关卡 1：防呛奶
  // ==========================================
  {
    id: 'q-sf1-1',
    levelId: 'newborn-safety-1',
    type: 'single',
    text: '瓶喂时，以下哪种做法最有助于预防呛奶？',
    options: [
      '选择大孔奶嘴让奶流得快一些',
      '让宝宝完全平躺着喝奶',
      '选择适合新生儿的小孔奶嘴，奶瓶适当倾斜让奶嘴充满奶液',
      '只在宝宝大哭的时候喂',
    ],
    correctAnswer: '选择适合新生儿的小孔奶嘴，奶瓶适当倾斜让奶嘴充满奶液',
    explanation: '小孔奶嘴控制流速、奶瓶适当倾斜确保奶嘴充满奶液而非空气、宝宝保持半竖立姿势，是预防呛奶的关键。',
  },
  {
    id: 'q-sf1-2',
    levelId: 'newborn-safety-1',
    type: 'scenario',
    text: '你在喂奶时宝宝突然呛咳起来，脸部憋红但能咳嗽出声，也能哭。你首先应该怎么做？',
    options: [
      '继续喂奶，让宝宝自己调整',
      '立即停止喂奶，让宝宝保持前倾姿势靠自身咳嗽排出，不要用力拍背',
      '把手指伸进宝宝嘴里把奶掏出来',
      '把宝宝竖直抱起用力拍后背',
    ],
    correctAnswer: '立即停止喂奶，让宝宝保持前倾姿势靠自身咳嗽排出，不要用力拍背',
    explanation: '宝宝能咳嗽、能出声说明气道未完全堵塞，应依靠宝宝自身的咳嗽反射排出。竖直抱起拍背或伸手指都可能导致奶液更深地落入气道。观察呼吸是否恢复正常。',
  },
  {
    id: 'q-sf1-5',
    levelId: 'newborn-safety-1',
    type: 'scenario',
    text: '喂奶时宝宝突然无法咳嗽、哭不出声、面色发青。你应该立即怎么做？',
    options: [
      '把宝宝竖直抱起拍后背',
      '将宝宝面部朝下放在前臂上（头低于躯干），在两肩胛骨之间用力拍击5次，同时呼叫急救',
      '把手指伸进宝宝喉咙里掏异物',
      '给喂水把奶冲下去',
    ],
    correctAnswer: '将宝宝面部朝下放在前臂上（头低于躯干），在两肩胛骨之间用力拍击5次，同时呼叫急救',
    explanation: '无法出声、面色发青是重度窒息的标志，需立即采用婴儿窒息急救法：面部朝下、头低于躯干、5次背击+5次胸压交替进行，同时拨打急救电话。禁止竖直抱起（会使异物更深）。',
  },
  {
    id: 'q-sf1-6',
    levelId: 'newborn-safety-1',
    type: 'truefalse',
    text: '宝宝呛奶时，竖直抱起用力拍后背是推荐的急救方法。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '竖直抱起拍背是危险做法，会导致奶液在重力作用下更深地落入气道。正确姿势是面部朝下、头低于躯干。轻度的应让宝宝自己咳出，重度的需采用背击+胸压急救法。',
  },
  {
    id: 'q-sf1-3',
    levelId: 'newborn-safety-1',
    type: 'truefalse',
    text: '喂完奶后让宝宝竖抱或保持半坐姿势 20-30 分钟，有助于减少溢奶和呛奶。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '喂奶后竖抱或半坐 20-30 分钟，利用重力帮助奶液留在胃中，减少溢奶和呛奶风险。拍嗝可在喂奶过程中进行。',
  },
  {
    id: 'q-sf1-4',
    levelId: 'newborn-safety-1',
    type: 'multiple',
    text: '以下哪些情况提示呛奶后需要立即就医？',
    options: [
      '持续咳嗽不止',
      '呼吸急促或困难',
      '嘴唇或面色发青',
      '呛奶后打了一个嗝',
    ],
    correctAnswer: ['持续咳嗽不止', '呼吸急促或困难', '嘴唇或面色发青'],
    explanation: '呛奶后如果出现持续咳嗽、呼吸困难、面色/口唇发青等表现，提示可能有奶液进入气道，需要立即拨打急救电话。',
  },
  {
    id: 'q-sf1-7',
    levelId: 'newborn-safety-1',
    type: 'single',
    text: '以下哪种奶嘴最容易导致新生儿呛奶？',
    options: ['低流速小孔奶嘴', '中流速中孔奶嘴', '高流速大孔奶嘴', '奶嘴孔大小不影响呛奶风险'],
    correctAnswer: '高流速大孔奶嘴',
    explanation: '奶嘴孔越大，奶液流速越快。新生儿吞咽-呼吸协调能力尚未成熟，流速过快时来不及吞咽，奶液容易误入气道。应选择适合新生儿的低流速（0-3月）小孔奶嘴。',
  },
  {
    id: 'q-sf1-8',
    levelId: 'newborn-safety-1',
    type: 'truefalse',
    text: '瓶喂时如果发现奶嘴孔太大流速太快导致宝宝来不及吞咽，可以更换为更小孔的奶嘴或放慢倾斜角度。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '新生儿需要使用低流速（0-3月）小孔奶嘴。如果喂奶时倾斜角度太大或奶嘴孔太大，奶液流速过快宝宝来不及吞咽容易呛奶。更换合适流速的奶嘴并控制奶瓶倾斜角度，是减少呛奶的有效方法。',
  },
  {
    id: 'q-sf1-9',
    levelId: 'newborn-safety-1',
    type: 'scenario',
    text: '你在用奶瓶喂宝宝，喂到一半突然奶流速变得很快，宝宝开始呛咳、表情痛苦。你发现是奶瓶倾斜角度太大了。以前也有几次类似经历。接下来你会如何调整？',
    options: [
      '继续同样方式喂，认为呛一呛就习惯了',
      '放低奶瓶角度控制流速，确认奶嘴孔大小适合新生儿，必要时更换更低流速的奶嘴',
      '停止瓶喂，改用勺喂',
      '加快喂奶速度让宝宝适应',
    ],
    correctAnswer: '放低奶瓶角度控制流速，确认奶嘴孔大小适合新生儿，必要时更换更低流速的奶嘴',
    explanation: '奶嘴流速过快是瓶喂呛奶的常见原因。应立即降低奶瓶倾斜角度让奶嘴中始终有奶但不会喷射式流出，并检查奶嘴孔大小是否匹配宝宝月龄（0-3月应使用S号或低速奶嘴）。这只是暂时的调整，随着宝宝长大吞咽协调能力增强通常会改善。',
  },
  {
    id: 'q-sf1-10',
    levelId: 'newborn-safety-1',
    type: 'truefalse',
    text: '宝宝呛奶时应该把手指伸入宝宝口中，把堵住喉咙的奶液抠出来。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '将手指伸入宝宝口中抠取是极其危险的做法。这会刺激咽部诱发呕吐反射，更可能将奶液或异物推入气道深处。正确的做法是：轻度呛咳让宝宝靠自身咳嗽排出；重度窒息采用标准婴儿窒息急救法（面朝下、头低于躯干、背击+胸压）。',
  },
  {
    id: 'q-sf1-11',
    levelId: 'newborn-safety-1',
    type: 'single',
    text: '新生儿呛奶的风险在以下哪个时期最高？',
    options: ['出生后第1-2周尤其明显', '3个月后', '6个月后', '呛奶风险与月龄无关'],
    correctAnswer: '出生后第1-2周尤其明显',
    explanation: '新生儿在出生后1-2周内吞咽-呼吸协调能力最弱，呛奶风险相对更高。随着神经系统发育，宝宝的吞咽-呼吸协调性逐步改善。但这不意味着之后就可以放松警惕——在整个婴儿期都应保持安全的喂养姿势和正确的喂养方式。',
  },
  {
    id: 'q-sf1-12',
    levelId: 'newborn-safety-1',
    type: 'multiple',
    text: '以下哪些做法有助于预防新生儿呛奶？',
    options: [
      '瓶喂时选择低流速小孔奶嘴',
      '喂奶时让宝宝保持半竖立姿势而非完全平躺',
      '瓶喂时控制奶瓶倾斜角度避免流速过快',
      '为了让宝宝喝得快，把奶瓶竖直倒立',
    ],
    correctAnswer: ['瓶喂时选择低流速小孔奶嘴', '喂奶时让宝宝保持半竖立姿势而非完全平躺', '瓶喂时控制奶瓶倾斜角度避免流速过快'],
    explanation: '预防呛奶的关键：选择合适流速的奶嘴（新生儿用 S 号/低速）、保持正确的半竖立喂养姿势、控制奶瓶倾斜角度以控制奶流速度、喂奶过程中观察宝宝状态。把奶瓶竖直倒立会极大加速流速，应避免。',
  },

  // ==========================================
  // 安全防护模块 - 关卡 2：防跌落
  // ==========================================
  {
    id: 'q-sf2-1',
    levelId: 'newborn-safety-2',
    type: 'truefalse',
    text: '新生儿还不会翻身，所以可以把他独自放在尿布更换台上几秒钟去拿东西。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '新生儿虽然不能主动翻身，但有不自主的肢体抽动和扭动，可能在瞬间从高处跌落。无论任何情况，都不能把宝宝独自留在高处。',
  },
  {
    id: 'q-sf2-2',
    levelId: 'newborn-safety-2',
    type: 'single',
    text: '婴儿床护栏栏杆间距应满足什么标准？',
    options: ['小于 6 厘米', '小于 10 厘米', '任意间距', '大于 10 厘米'],
    correctAnswer: '小于 6 厘米',
    explanation: '护栏栏杆间距必须小于 6 厘米，以防宝宝的头伸入栏杆之间被卡住。这是婴儿床的重要安全标准。',
  },
  {
    id: 'q-sf2-3',
    levelId: 'newborn-safety-2',
    type: 'scenario',
    text: '朋友送了一张二手婴儿床，说是几年前买的但没用过几次。你该怎么做？',
    options: [
      '直接使用，看起来很新',
      '检查生产日期是否在安全标准日期之后、是否曾被召回、所有部件是否完好，确认安全再使用',
      '加装防撞护垫和厚床围确保安全',
      '只能买全新的，二手的不可能安全',
    ],
    correctAnswer: '检查生产日期是否在安全标准日期之后、是否曾被召回、所有部件是否完好，确认安全再使用',
    explanation: '使用二手婴儿床需仔细检查：是否符合现行安全标准、是否曾被厂家召回、五金件和木质部分有无损坏。不符合标准的产品不应使用。',
  },
  {
    id: 'q-sf2-4',
    levelId: 'newborn-safety-2',
    type: 'multiple',
    text: '预防婴儿床跌落，以下哪些做法是正确的？',
    options: [
      '婴儿床不要放置在窗户旁边',
      '床垫必须与婴儿床内部尺寸完全匹配',
      '宝宝能坐起后降低床板高度',
      '在婴儿床四周安装厚实的防撞护垫',
    ],
    correctAnswer: ['婴儿床不要放置在窗户旁边', '床垫必须与婴儿床内部尺寸完全匹配', '宝宝能坐起后降低床板高度'],
    explanation: '婴儿床应远离窗户，床垫与床架紧密贴合，及时降低床板。AAP 不推荐安装防撞护垫——它不仅不能有效防撞，反而可能造成窒息或被缠住的风险。',
  },
  {
    id: 'q-sf2-5',
    levelId: 'newborn-safety-2',
    type: 'single',
    text: '换尿布时最重要的安全原则是？',
    options: [
      '尿布更换台放在最高处方便操作',
      '始终用一只手扶着宝宝，绝不转身离开',
      '用安全绑带固定在尿布台上，绑带可以替代看护',
      '只要宝宝还不会翻身就安全',
    ],
    correctAnswer: '始终用一只手扶着宝宝，绝不转身离开',
    explanation: '新生儿即使不会主动翻身，也可能因不自主的肢体抽动突然滚落。换尿布时必须始终用一只手扶着宝宝。如果需要拿东西，要么先拿好在手边，要么抱起宝宝一起去。没有任何设备能替代看护。',
  },
  {
    id: 'q-sf2-6',
    levelId: 'newborn-safety-2',
    type: 'scenario',
    text: '在给宝宝换尿布时，你发现忘拿了放在房间另一头的护臀膏。宝宝刚3周大，还不会翻身。你该怎么做？',
    options: [
      '让宝宝在尿布台上等，自己快速跑过去拿',
      '抱着宝宝一起去拿护臀膏，再回来继续换',
      '今天不用护臀膏了',
      '把尿布台推到护臀膏旁边',
    ],
    correctAnswer: '抱着宝宝一起去拿护臀膏，再回来继续换',
    explanation: '即使只有几秒钟，也不能将宝宝独自留在尿布台或任何高处。一个常见的严重意外就是"我只离开了一下"。需要离开时抱起宝宝一起去，哪怕只是去房间里拿个东西。建议换尿布前就准备好所有需要的东西在伸手可及的地方。',
  },
  {
    id: 'q-sf2-7',
    levelId: 'newborn-safety-2',
    type: 'truefalse',
    text: '在床上或沙发上喂完夜奶后，让宝宝就睡在大床中间（父母睡两侧）是最方便的。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '成人床垫通常太软、可能有枕头和厚被子，这些对新生儿来说存在严重的窒息和跌落风险。如果喂夜奶时非常疲惫，最安全的方式是喂完后把宝宝放回自己的婴儿床或摇篮中。为防止喂奶时睡着，可设置闹钟或让伴侣帮助监督。',
  },
  {
    id: 'q-sf2-8',
    levelId: 'newborn-safety-2',
    type: 'single',
    text: '以下关于婴儿床床垫与床架配合的说法哪项正确？',
    options: [
      '床垫与床架之间留一指宽的缝隙利于通风',
      '床垫必须与婴儿床内部尺寸完全匹配，边缘不留缝隙',
      '床垫略小一些让宝宝有空间活动',
      '缝隙大小无关紧要',
    ],
    correctAnswer: '床垫必须与婴儿床内部尺寸完全匹配，边缘不留缝隙',
    explanation: '床垫与床架之间的缝隙可能导致宝宝被卡住或导致头颈陷入而窒息。标准要求：当床垫完全贴合一侧时，另一边缝隙不能放入两根手指（约3cm）。购买婴儿床时建议床垫和床架同一品牌配套购买。',
  },
  {
    id: 'q-sf2-9',
    levelId: 'newborn-safety-2',
    type: 'truefalse',
    text: '婴儿床四周安装厚防撞护垫可以保护宝宝不撞头，是推荐的安全措施。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: 'AAP 明确不推荐使用婴儿床防撞护垫。防撞护垫实际上很少能预防严重撞伤（婴儿撞到护栏的力量通常很小），反而带来更大的窒息和缠绕风险。宝宝可能会把脸抵在护垫上导致呼吸受阻，或被护垫的绑绳缠住。市场上不少国家已禁止销售防撞护垫。',
  },
  {
    id: 'q-sf2-10',
    levelId: 'newborn-safety-2',
    type: 'scenario',
    text: '你推着婴儿车带宝宝散步，路过一段不太平整的人行道。以下哪种做法最安全？',
    options: [
      '加快速度推过去，颠簸一下没关系',
      '放慢速度、两只手握紧推车把手、确保宝宝系好安全带，小心通过',
      '把宝宝从推车里抱出来，一手抱娃一手推车',
      '在推车下垫毛巾减震',
    ],
    correctAnswer: '放慢速度、两只手握紧推车把手、确保宝宝系好安全带，小心通过',
    explanation: '在不平整路面推婴儿车时应减速、双手握把手、确保宝宝使用安全带固定。一手抱娃一手推车在颠簸路面极易失稳导致宝宝跌落。每次使用推车前都应检查安全带是否扣好。',
  },
  {
    id: 'q-sf2-11',
    levelId: 'newborn-safety-2',
    type: 'multiple',
    text: '以下哪些是婴儿房/婴儿床区域的安全要求？',
    options: [
      '婴儿床不要放在窗户旁边',
      '窗帘拉绳、百叶窗绳等带状物应远离婴儿床',
      '婴儿床上方不悬挂画框或搁板等可能坠落的物品',
      '婴儿床可以放在暖气片旁边取暖',
    ],
    correctAnswer: ['婴儿床不要放在窗户旁边', '窗帘拉绳、百叶窗绳等带状物应远离婴儿床', '婴儿床上方不悬挂画框或搁板等可能坠落的物品'],
    explanation: '窗户旁有坠落和窗帘绳缠绕风险；窗帘拉绳是勒颈危险；头上方的悬挂物可能坠落。婴儿床应远离暖气片和加热器（过热和烫伤风险）。安全布置的核心是：以婴儿的视角检查房间，排除一切可触及的危险。',
  },
  {
    id: 'q-sf2-12',
    levelId: 'newborn-safety-2',
    type: 'single',
    text: '关于抱着宝宝上下楼梯，以下哪项是正确的？',
    options: [
      '一手抱宝宝、一手拿着东西上下楼没问题',
      '上下楼梯一定要扶着扶手，不要一手抱娃一手拿东西',
      '让宝宝自己扶栏杆上楼梯能锻炼大运动',
      '楼梯上不用装安全门，大人看着就行',
    ],
    correctAnswer: '上下楼梯一定要扶着扶手，不要一手抱娃一手拿东西',
    explanation: '抱着宝宝上下楼梯时失足是常见的家庭意外。务必扶着扶手保持平衡，不要单手抱娃同时另一手拿东西（手机、奶瓶等）。需要在两层楼都有常用物品，减少抱娃上下楼的次数。宝宝稍大后应在楼梯顶部（和底部，如果地下室）安装安全门。',
  },

  // ==========================================
  // 安全防护模块 - 关卡 3：安全抱姿
  // ==========================================
  {
    id: 'q-sf3-1',
    levelId: 'newborn-safety-3',
    type: 'single',
    text: '抱起新生儿时，手应该如何支撑？',
    options: [
      '一只手从腋下抱起即可',
      '一只手托住头颈，另一只手托住臀部',
      '抱着宝宝的腰就可以了',
      '两只手托住腋下',
    ],
    correctAnswer: '一只手托住头颈，另一只手托住臀部',
    explanation: '新生儿颈部肌肉还不能支撑头部，抱起时必须托住头颈和臀部两个关键部位。放下时先让身体着床，再轻轻放开头颈。',
  },
  {
    id: 'q-sf3-2',
    levelId: 'newborn-safety-3',
    type: 'truefalse',
    text: '宝宝哭闹哄不住时，可以轻轻把宝宝往空中抛接，这样他就不哭了。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '抛接、转圈、用力摇晃对新生儿非常危险，可能造成摇晃婴儿综合征——脑血管撕裂、颅内出血，导致永久性损伤甚至死亡。',
  },
  {
    id: 'q-sf3-3',
    levelId: 'newborn-safety-3',
    type: 'scenario',
    text: '你使用婴儿背带带宝宝出门。家人帮你把宝宝放进去后，你发现宝宝的脸完全埋在布料里，看不到宝宝的面部。你该怎么做？',
    options: [
      '没关系，这样宝宝更暖和',
      '重新调整背带，确保能看到宝宝的脸、下巴不贴近胸口、呼吸道通畅',
      '给宝宝围一条围巾就好了',
      '让家人来调整就行',
    ],
    correctAnswer: '重新调整背带，确保能看到宝宝的脸、下巴不贴近胸口、呼吸道通畅',
    explanation: '使用背带必须确保：始终能看到宝宝的脸、宝宝呼吸通畅、下巴不贴近胸口（避免呼吸道受压）。这是防止背带内窒息的关键安全原则。',
  },
  {
    id: 'q-sf3-4',
    levelId: 'newborn-safety-3',
    type: 'multiple',
    text: '关于抱新生儿的正确做法，以下哪些是正确的？',
    options: [
      '抱起和放下时始终支撑头部和颈部',
      '不要只从腋下抱起新生儿',
      '如果感到情绪激动，先放下宝宝冷静再抱',
      '抱着宝宝上下楼梯时不要同时拿东西',
    ],
    correctAnswer: ['抱起和放下时始终支撑头部和颈部', '不要只从腋下抱起新生儿', '如果感到情绪激动，先放下宝宝冷静再抱', '抱着宝宝上下楼梯时不要同时拿东西'],
    explanation: '以上四项都是安全抱姿的重要原则。特别要强调的是：当自己被宝宝哭闹搞得烦躁时，把宝宝安全放下、自己冷静后再去安抚，是保护宝宝的重要方式。',
  },
  {
    id: 'q-sf3-5',
    levelId: 'newborn-safety-3',
    type: 'single',
    text: '使用婴儿背带时，T.I.C.K.S 原则中的 "K" 代表什么？',
    options: [
      'Keep baby warm（保持宝宝暖和）',
      'Keep chin off chest（下巴不贴近胸口）',
      'Kiss baby frequently（经常亲吻宝宝）',
      'Keep straps tight（保持绑带绷紧）',
    ],
    correctAnswer: 'Keep chin off chest（下巴不贴近胸口）',
    explanation: 'T.I.C.K.S 代表：Tight（背带紧贴）、In view at all times（始终能看到宝宝）、Close enough to kiss（近到可以亲吻头顶）、Keep chin off chest（下巴不贴胸口保持呼吸道通畅）、Supported back（背部有良好支撑）。其中 K 原则尤为关键——宝宝下巴贴近胸口会压迫气道导致窒息。',
  },
  {
    id: 'q-sf3-6',
    levelId: 'newborn-safety-3',
    type: 'truefalse',
    text: '使用婴儿背带时，只要能牢固地把宝宝绑住就安全了，不需要额外检查宝宝的呼吸道。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '背带窒息是实际发生过的事故。宝宝在背带中可能因为头颈前倾导致气道受压，而照护者不易察觉。每次使用背带都必须确认：能看到宝宝的脸、鼻子和嘴巴没有被布料遮挡、下巴没有贴近胸口。每 15-20 分钟应检查一次宝宝的状态。',
  },
  {
    id: 'q-sf3-7',
    levelId: 'newborn-safety-3',
    type: 'scenario',
    text: '宝宝连续哭了 40 分钟，你试了喂奶、换尿布、抱着走、唱歌都没用。你开始感到焦躁，手臂不自觉地把宝宝抱得更紧。隔壁房间的伴侣还没下班回家。你此刻最应该做什么？',
    options: [
      '继续摇晃宝宝，直到他安静下来',
      '把宝宝安全地放在婴儿床中，自己到另一个房间冷静5-10分钟，调整好情绪再回来',
      '大声对宝宝说"不要再哭了"',
      '抱着宝宝一起哭一场',
    ],
    correctAnswer: '把宝宝安全地放在婴儿床中，自己到另一个房间冷静5-10分钟，调整好情绪再回来',
    explanation: '宝宝哭闹是新手父母最大的压力来源之一，感到焦躁非常正常。但情绪激动时摇晃或用力抱紧宝宝可能造成不可逆的伤害。正确的做法是：把宝宝安全放在婴儿床里→自己到另一个房间冷静→深呼吸→等情绪平复后（通常5-10分钟）再回去。宝宝在安全的婴儿床里哭一会儿是没关系的。如果你频繁感到无法控制情绪，请寻求家人支持和专业帮助。',
  },
  {
    id: 'q-sf3-8',
    levelId: 'newborn-safety-3',
    type: 'single',
    text: '以下关于摇晃婴儿综合征的说法，哪项是正确的？',
    options: [
      '只有猛力摇晃才会造成伤害，轻晃没事',
      '摇晃可导致脑血管撕裂、视网膜出血和永久性脑损伤，任何程度都不应故意摇晃',
      '摇晃主要伤害骨骼，对大脑影响不大',
      '只有在虐待情形下的摇晃才会造成伤害',
    ],
    correctAnswer: '摇晃可导致脑血管撕裂、视网膜出血和永久性脑损伤，任何程度都不应故意摇晃',
    explanation: '摇晃婴儿综合征是一种严重的脑损伤。婴儿头部相对较大且重（占体重的25%）、颈部肌肉很弱、脑组织含水量高且髓鞘化不足，剧烈摇晃时大脑在颅骨内来回撞击，导致脑血管撕裂、脑内出血和视网膜出血。后果包括永久性残疾（视力障碍、智力障碍、癫痫）甚至死亡。即使是"玩闹式"的抛接、转圈也十分危险。',
  },
  {
    id: 'q-sf3-9',
    levelId: 'newborn-safety-3',
    type: 'scenario',
    text: '你准备带 6 周大的宝宝出门，第一次使用新买的婴儿背带。穿戴好后你发现需要通过镜子才能勉强看到宝宝的头顶。你应该怎么做？',
    options: [
      '就这样出门，到外面让家人帮忙看看',
      '重新调整背带高度，确保低头就能直接看到宝宝的脸，并且宝宝下巴没有贴近胸口',
      '这是正常的，所有背带都看不到宝宝的脸',
      '让宝宝脸朝外就能看到了',
    ],
    correctAnswer: '重新调整背带高度，确保低头就能直接看到宝宝的脸，并且宝宝下巴没有贴近胸口',
    explanation: '使用婴儿背带的"可见性原则"：你应该无需借助镜子、低头就能看到宝宝的脸。如果看不到，背带位置可能过高或过低。调整到"close enough to kiss"（近到可亲吻头顶）的位置。新生儿脸朝外（face out）会增加背部压力和过度刺激的风险，通常建议等到宝宝有较强头控能力（约5-6个月）后才考虑。',
  },
  {
    id: 'q-sf3-10',
    levelId: 'newborn-safety-3',
    type: 'single',
    text: '正确的放下宝宝（放入婴儿床）的动作是？',
    options: [
      '先让宝宝的脚着床，然后松手',
      '先让宝宝的整个身体侧着落床，再放开托头的手',
      '先让宝宝臀部着床，然后身体落下，最后轻轻放开托头颈的手',
      '直接悬空放手让宝宝自然落到床上',
    ],
    correctAnswer: '先让宝宝臀部着床，然后身体落下，最后轻轻放开托头颈的手',
    explanation: '放下宝宝的顺序与抱起相反：始终保持对头颈的支撑直到宝宝完全安稳地躺在床面上。具体动作：先将宝宝降低到床上方→先让臀部接触床面→身体跟着落下→最后才轻轻放开托头颈的手。这可以防止宝宝因突然失去支撑而惊跳或扭伤。',
  },
  {
    id: 'q-sf3-11',
    levelId: 'newborn-safety-3',
    type: 'multiple',
    text: '关于婴儿背带的安全使用，以下哪些是正确的？',
    options: [
      '使用前检查所有卡扣、缝线和布料是否完好',
      '确保宝宝的脸始终可见，不被布料遮挡',
      '背带内的温度可能比外界高很多，应注意避免宝宝过热',
      '新生儿面部朝向背带穿戴者（面对妈妈/爸爸的胸口）是最安全的位置',
    ],
    correctAnswer: ['使用前检查所有卡扣、缝线和布料是否完好', '确保宝宝的脸始终可见，不被布料遮挡', '背带内的温度可能比外界高很多，应注意避免宝宝过热', '新生儿面部朝向背带穿戴者（面对妈妈/爸爸的胸口）是最安全的位置'],
    explanation: '背带安全的四个核心：①每次使用前检查设备完整性；②可见性（始终能看到宝宝全脸）；③温度管理（背带+多层衣物+大人体温可能导致过热，天热时需特别注意）；④对新生儿而言，面向抱持者胸口的位置最安全。',
  },
  {
    id: 'q-sf3-12',
    levelId: 'newborn-safety-3',
    type: 'truefalse',
    text: '当感到宝宝哭闹让你烦躁时，把宝宝放在安全的婴儿床里然后自己离开房间冷静，是正确的做法。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '这是预防摇晃婴儿综合征的核心策略之一。当照护者情绪失控时，宝宝在安全的婴儿床里（仰卧、床上无松软物品）哭一会儿是安全的。照护者去隔壁房间深呼吸、喝水、听几分钟音乐冷静下来后再回来是最好的选择。可以和家人提前约定一个"暗号"，比如"我需要你接手一下"，让另一方帮忙分担。',
  },

  // ==========================================
  // 发育观察模块 - 关卡 1：黄疸观察
  // ==========================================
  {
    id: 'q-d1-1',
    levelId: 'newborn-dev-1',
    type: 'single',
    text: '新生儿黄疸的生理性原因是什么？',
    options: [
      '肝脏有病',
      '新生儿红细胞寿命短、肝脏处理胆红素功能尚未成熟',
      '喝了不干净的水',
      '妈妈吃了黄色食物',
    ],
    correctAnswer: '新生儿红细胞寿命短、肝脏处理胆红素功能尚未成熟',
    explanation: '新生儿黄疸主要是生理性的：红细胞寿命短于成人、肝脏转化胆红素的功能尚未发育成熟、肠道重吸收导致胆红素在血液中蓄积。大多数在 1-2 周内自然消退。',
  },
  {
    id: 'q-d1-2',
    levelId: 'newborn-dev-1',
    type: 'truefalse',
    text: '观察宝宝黄疸应在自然光下，用手指轻压皮肤后观察底色。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '自然光下用手指轻压皮肤（如额头、鼻梁、胸骨），看按压后皮肤的底色黄染程度，是居家观察黄疸的正确方法。灯光下观察不准确。',
  },
  {
    id: 'q-d1-3',
    levelId: 'newborn-dev-1',
    type: 'multiple',
    text: '以下哪些属于新生儿黄疸的危险信号？',
    options: [
      '出生 24 小时内就出现黄疸',
      '黄疸从面部逐渐发展到胸部',
      '宝宝精神萎靡、嗜睡、吸吮无力',
      '大便颜色变浅呈陶土色',
    ],
    correctAnswer: ['出生 24 小时内就出现黄疸', '宝宝精神萎靡、嗜睡、吸吮无力', '大便颜色变浅呈陶土色'],
    explanation: '危险信号包括：过早出现（24小时内）、精神差、大便变浅。黄疸从头到脚发展是正常规律，但过快发展到下半身需警惕。',
  },
  {
    id: 'q-d1-4',
    levelId: 'newborn-dev-1',
    type: 'scenario',
    text: '宝宝出生第 3 天，面部和胸部看起来有些发黄，但精神好、吃奶正常、大便黄色。你该怎么做？',
    options: [
      '不用管，自己会好',
      '继续观察宝宝的吃奶、精神状态和黄疸范围变化，按计划复查',
      '立即去医院照蓝光',
      '停止正常喂养改为特殊奶粉',
    ],
    correctAnswer: '继续观察宝宝的吃奶、精神状态和黄疸范围变化，按计划复查',
    explanation: '出生后 2-4 天出现、仅面胸部发黄、精神和吃奶正常的黄疸多为生理性黄疸。应持续观察，关注是否向身体下部蔓延、精神状态有无变化。如果有担忧，咨询医生。',
  },
  {
    id: 'q-d1-5',
    levelId: 'newborn-dev-1',
    type: 'single',
    text: '治疗严重新生儿黄疸最常用且有效的方法是什么？',
    options: ['晒太阳', '多喝水', '光疗（蓝光照射）', '吃药'],
    correctAnswer: '光疗（蓝光照射）',
    explanation: '光疗是治疗严重新生儿黄疸的标准方法。特殊波长的蓝光照射可以将皮肤中的胆红素转化为可经尿液排出的形式。晒太阳不能替代医疗光疗，且可能造成日晒伤。',
  },
  {
    id: 'q-d1-6',
    levelId: 'newborn-dev-1',
    type: 'single',
    text: '黄疸通常按什么顺序在身体上进展？',
    options: [
      '从脚到头发展、从头到脚消退',
      '从头到脚发展、从脚到头消退',
      '同时全身均匀变黄',
      '先从手脚心开始变黄',
    ],
    correctAnswer: '从头到脚发展、从脚到头消退',
    explanation: '黄疸遵循"克拉默法则"的进展规律：通常先出现在面部和头部→向下蔓延到胸部、腹部→再到四肢→严重时才到手心脚心。消退顺序恰好相反——脚心恢复正常、最后面部退黄。因此黄疸到了下半身说明程度已经不轻，需要引起重视和就医评估。',
  },
  {
    id: 'q-d1-7',
    levelId: 'newborn-dev-1',
    type: 'truefalse',
    text: '新生儿黄疸消退速度因人而异，有些宝宝的黄疸可能持续数周，只要精神好、吃奶正常、体重增长正常，通常属于良性过程。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '新生儿黄疸有生理性和病理性之分。生理性黄疸通常在出生后 2-4 天出现，约 1-2 周内自然消退。但也有部分宝宝的黄疸持续时间较长（称为迁延性黄疸），只要宝宝精神好、喂养正常、体重增长正常、大便颜色正常（黄色），通常是良性的，关键是由医生排除病理性黄疸。',
  },
  {
    id: 'q-d1-8',
    levelId: 'newborn-dev-1',
    type: 'scenario',
    text: '宝宝出生第 10 天，奶粉喂养，体重已经恢复出生体重。但面部和胸部仍有明显黄疸，大便颜色正常（黄色）。社区医生建议换特殊奶粉"退黄"。你该怎么办？',
    options: [
      '立即更换特殊奶粉',
      '继续正常喂养，但同时咨询儿科医生做胆红素水平检测，排除病理性黄疸后再决定',
      '多喂水帮宝宝退黄',
      '每天抱着宝宝晒太阳 1 小时',
    ],
    correctAnswer: '继续正常喂养，但同时咨询儿科医生做胆红素水平检测，排除病理性黄疸后再决定',
    explanation: '新生儿黄疸绝大多数是生理性的。正确做法是：请儿科医生做胆红素水平检测（从脚跟微量采血），判断黄疸程度和性质。如果是生理性/良性迁延性黄疸，继续正常喂养；如果胆红素水平达到光疗标准，遵医嘱治疗。晒太阳和喂水都不可靠，且晒太阳有晒伤风险。',
  },
  {
    id: 'q-d1-9',
    levelId: 'newborn-dev-1',
    type: 'single',
    text: '早期识别胆道闭锁的关键线索是观察宝宝的大便颜色。什么是需要警惕的异常大便颜色？',
    options: [
      '黄色稀便',
      '绿色糊状便',
      '陶土色或灰白色大便',
      '棕色成条便',
    ],
    correctAnswer: '陶土色或灰白色大便',
    explanation: '胆道闭锁是一种罕见但严重的新生儿疾病，胆汁无法排入肠道，导致大便不含胆色素而呈陶土色、灰白色或浅黄色。这是一种需要早期手术的急症。所有新生儿父母应关注大便颜色——如果大便持续呈异常浅色（不是黄色、绿色或棕色），应立即就医。许多国家将大便颜色卡纳入新生儿出院宣教材料。',
  },
  {
    id: 'q-d1-10',
    levelId: 'newborn-dev-1',
    type: 'scenario',
    text: '宝宝出生第 4 天，黄疸从面部蔓延到胸腹部，今天发现大腿也开始有些发黄。精神状态比昨天略差，吃奶时间也变短了。你该怎么做？',
    options: [
      '继续在家观察，可能是正常的黄疸加重期',
      '立即带宝宝就医做胆红素检测，评估是否需要光疗',
      '给宝宝多喝水看能不能退黄',
      '等到下周定期检查再问医生',
    ],
    correctAnswer: '立即带宝宝就医做胆红素检测，评估是否需要光疗',
    explanation: '三个警示信号需要立即就医：①黄疸迅速向身体下半部分蔓延（到了大腿）；②精神状态变差（嗜睡）；③吃奶减少。这些提示胆红素水平可能在快速上升，可能已达到需要光疗的程度。严重未治疗的高胆红素可导致胆红素脑病（核黄疸），造成永久性神经损伤。不要等待——今天就去。',
  },
  {
    id: 'q-d1-11',
    levelId: 'newborn-dev-1',
    type: 'truefalse',
    text: '早产儿（胎龄小于 37 周）发生严重黄疸的风险高于足月儿，需要更严密的监测。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '早产儿的肝脏处理胆红素的能力更不成熟，血脑屏障也更脆弱，在更低的胆红素水平下就可能发生脑损伤。因此早产儿黄疸的治疗阈值（启动光疗的胆红素值）比足月儿更低，监测应更频繁。早产儿家庭出院后应遵医嘱定期复查胆红素。',
  },
  {
    id: 'q-d1-12',
    levelId: 'newborn-dev-1',
    type: 'multiple',
    text: '以下哪些是新生儿黄疸需要立即就医的警示信号？',
    options: [
      '出生 24 小时内出现黄疸',
      '黄疸迅速蔓延至腹部和下肢',
      '宝宝精神萎靡、嗜睡、吃奶无力',
      '大便颜色变为黄色',
    ],
    correctAnswer: ['出生 24 小时内出现黄疸', '黄疸迅速蔓延至腹部和下肢', '宝宝精神萎靡、嗜睡、吃奶无力'],
    explanation: '这三个是核心警示信号：过早出现（<24小时）、进展太快、伴随神经症状。注意：大便颜色变为黄色是好事（说明胆汁进入肠道），需要警惕的是大便变浅呈陶土色/灰白色。此外，出生 2 周后黄疸仍不退或反而加重也需就医。',
  },

  // ==========================================
  // 发育观察模块 - 关卡 2：体重变化
  // ==========================================
  {
    id: 'q-d2-1',
    levelId: 'newborn-dev-2',
    type: 'single',
    text: '足月健康新生儿出生后生理性体重下降的正常范围是多少？',
    options: ['5%-10%', '15%-20%', '25%', '不应下降'],
    correctAnswer: '5%-10%',
    explanation: '出生后前几天体重下降 7%-10% 以内是正常的生理现象，主要原因是体内多余水分的排出和胎便的排泄。超过 10% 需医生关注。',
  },
  {
    id: 'q-d2-2',
    levelId: 'newborn-dev-2',
    type: 'truefalse',
    text: '新生儿恢复出生体重后，头 3 个月每天大约增长 14-28 克。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '恢复出生体重后，新生儿在头3个月平均每天增长约14-28克（每周约100-200克），但存在个体差异，关键看整体生长趋势。',
  },
  {
    id: 'q-d2-3',
    levelId: 'newborn-dev-2',
    type: 'scenario',
    text: '宝宝出生第 5 天，比出生时体重下降了 8%，但吃奶有力、每天有 6 片湿尿布、精神状态良好。妈妈的奶水刚刚下来。你该怎么看待这个情况？',
    options: [
      '体重下降 8% 属于生理范围，继续观察喂养状态',
      '立即添加配方奶补充营养',
      '这很危险，马上去医院',
      '减少喂奶次数，让宝宝体重恢复',
    ],
    correctAnswer: '体重下降 8% 属于生理范围，继续观察喂养状态',
    explanation: '下降 8% 在正常生理范围（7%-10%）内，且宝宝吃奶、排泄、精神状态都好，继续正常喂养和观察即可。如果超过 10% 或 2 周仍未恢复出生体重，应就医。',
  },
  {
    id: 'q-d2-4',
    levelId: 'newborn-dev-2',
    type: 'multiple',
    text: '以下哪些表现提示宝宝可能存在体重增长不良，需要就医评估？',
    options: [
      '出生 2 周后仍低于出生体重',
      '每天湿尿布少于 4 片',
      '宝宝长期嗜睡、吸吮无力',
      '体重增长速度在生长曲线 15 百分位（在正常范围内但有下降趋势）',
    ],
    correctAnswer: ['出生 2 周后仍低于出生体重', '每天湿尿布少于 4 片', '宝宝长期嗜睡、吸吮无力'],
    explanation: '2周仍未恢复出生体重、尿布减少、精神差、喂养困难都是需要关注的信号。单一的生长百分位不是问题，重要的是整体趋势和多种指标的交叉验证。',
  },
  {
    id: 'q-d2-5',
    levelId: 'newborn-dev-2',
    type: 'single',
    text: '新生儿出生后第一周，建议多久称一次体重来监测生长趋势？',
    options: [
      '每天一次',
      '每 2-3 天一次',
      '每周一次即可',
      '每两周一次',
    ],
    correctAnswer: '每 2-3 天一次',
    explanation: '出生第一周是体重变化最关键的阶段，每2-3天称一次可以及时发现体重下降是否超过10%的警戒线。过于频繁（每天）容易引起不必要的焦虑，间隔太久则可能错过干预时机。',
  },
  {
    id: 'q-d2-6',
    levelId: 'newborn-dev-2',
    type: 'truefalse',
    text: '判断：家用体重秤可以精确监测新生儿的体重变化，不需要专门的婴儿秤。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '家用成人秤的精度通常在100g左右，而新生儿每天增长仅14-28g，无法准确监测。应使用精度至少10g的婴儿秤，且在相同条件下称重（如脱光衣服、固定时间），才能获得可靠的生长趋势。',
  },
  {
    id: 'q-d2-7',
    levelId: 'newborn-dev-2',
    type: 'single',
    text: '关于新生儿生长曲线图，以下说法正确的是？',
    options: [
      '只要在 50 百分位以上就是健康的',
      '低于 15 百分位就一定是营养不良',
      '百分位只是统计学工具，更重要的是宝宝的个体生长趋势',
      '生长曲线图只对早产儿有意义',
    ],
    correctAnswer: '百分位只是统计学工具，更重要的是宝宝的个体生长趋势',
    explanation: '生长曲线是统计分布图，15百分位只是说在100个同龄宝宝中有15个体重更轻，并不等于"异常"。关键看宝宝的生长是否沿自身百分位曲线平稳上升，以及综合喂养、排泄、精神状态等多维指标。',
  },
  {
    id: 'q-d2-8',
    levelId: 'newborn-dev-2',
    type: 'single',
    text: '配方奶喂养的新生儿体重增长应参考什么标准来评估？',
    options: [
      '和邻居家的宝宝比较',
      '对照 WHO 或本国儿科推荐的新生儿生长曲线，关注宝宝的个体增长趋势',
      '长得越快越好',
      '只要不生病就不用关注体重',
    ],
    correctAnswer: '对照 WHO 或本国儿科推荐的新生儿生长曲线，关注宝宝的个体增长趋势',
    explanation: '新生儿体重评估金标准是使用 WHO 或本国儿科推荐的生长曲线图，关注宝宝是否沿自身百分位曲线平稳增长。出生后前几天体重下降 7%-10% 属正常，通常在 7-10 天恢复出生体重，之后前 3 个月每天约增长 20-30 克。',
  },
  {
    id: 'q-d2-9',
    levelId: 'newborn-dev-2',
    type: 'single',
    text: '对于早产儿（胎龄<37周）的体重监测，以下做法正确的是？',
    options: [
      '使用足月儿的生长标准进行评估',
      '使用矫正月龄对照专门的早产儿生长曲线',
      '早产儿不需要监测体重，能吃就行',
      '必须达到足月儿体重才能出院',
    ],
    correctAnswer: '使用矫正月龄对照专门的早产儿生长曲线',
    explanation: '早产儿应使用矫正月龄（从预产期起算而非实际出生日期）对照专门的早产儿生长曲线（如Fenton曲线），直到矫正月龄24-36个月后可转为标准生长曲线。直接使用足月儿标准会导致不必要的焦虑和过度干预。',
  },
  {
    id: 'q-d2-10',
    levelId: 'newborn-dev-2',
    type: 'scenario',
    text: '宝宝出生18天，体重较出生时增长了150g，平均每天增长约8g。宝宝现在每天喝配方奶约 400ml，每天有5-6片湿尿布，精神状态好。婆婆说"邻居家的宝宝一天长一两，我们家的长得太慢"，要求换奶粉或增加奶量。你的建议是？',
    options: [
      '立刻大幅增加单次喂养量追赶体重',
      '每天增重8g虽然偏低但在可接受范围，结合尿量充足和精神好，继续当前喂养方案并密切观察趋势',
      '减少喂奶次数以延长单次喂养量',
      '换用早产儿配方奶加速增长',
    ],
    correctAnswer: '每天增重8g虽然偏低但在可接受范围，结合尿量充足和精神好，继续当前喂养方案并密切观察趋势',
    explanation: '虽然平均日增重8g略低于理想的14-28g，但单个时间点的数据需结合整体评估：尿量充足说明摄入基本足够，精神好排除病理性问题。建议增加称重频率至每天一次连续3-5天看趋势，同时评估喂养效率（吞咽、瓶喂姿势）。不应因单一数据就贸然改变喂养方案。',
  },
  {
    id: 'q-d2-11',
    levelId: 'newborn-dev-2',
    type: 'multiple',
    text: '以下哪些客观指标可以用来综合评估新生儿的喂养是否充足？（多选）',
    options: [
      '每天湿尿布数量（≥6片为充足）',
      '大便颜色从胎便转为黄色奶便',
      '喂奶后宝宝主动松开奶嘴并满足安静',
      '每天用婴儿秤称重并纠结于±10g的波动',
    ],
    correctAnswer: ['每天湿尿布数量（≥6片为充足）', '大便颜色从胎便转为黄色奶便', '喂奶后宝宝主动松开奶嘴并满足安静'],
    explanation: '评估喂养充足性应使用多维指标：尿量（输出）、大便转变（消化启动）、喂养后的满足感（主动松开奶嘴并安静满足）。每天纠结于±10g的体重波动是不可靠的，因为单次体重受排便、喂奶时间等多因素干扰，短时间内的波动不代表真实生长趋势。',
  },
  {
    id: 'q-d2-12',
    levelId: 'newborn-dev-2',
    type: 'scenario',
    text: '宝宝出生第7天，体重从出生时的3200g降至2880g（下降10%），第8天2860g（下降10.6%），第9天2850g（下降10.9%）。宝宝现在配方奶喂养，吸吮力偏弱，每次喂奶经常睡着。此时你应该？',
    options: [
      '继续观察，再等一周看看',
      '立即就医并评估喂养状况，医生评估后可能建议调整喂养方案',
      '自行购买高能量配方奶喂养',
      '减少喂养频率让宝宝饿了自己吃',
    ],
    correctAnswer: '立即就医并评估喂养状况，医生评估后可能建议调整喂养方案',
    explanation: '体重下降超过10%且持续3天未回升，同时伴有吸吮力弱、喂奶易睡着，这是需要立即就医的警示信号。医生会评估脱水状况、检查是否有病理性因素（如感染、代谢问题），并可能建议调整喂养方案或制定追赶生长计划。自行更改喂养方案存在过度喂养或延误诊断的风险。',
  },

  // ############################################
  // 补充题库：每个关卡扩充至 12 题
  // ############################################

  // ==========================================
  // 喂养模块 - 关卡 1 补充（已有8题，+4）
  // ==========================================
  {
    id: 'q-f1-8',
    levelId: 'newborn-feeding-1',
    type: 'single',
    text: '配方奶喂养的宝宝需要额外补充维生素 D 吗？',
    options: ['不需要，配方奶中已含足量维生素 D', '需要每天额外补充 400 IU，和纯母乳喂养的宝宝一样', '需要补充 800 IU', '只有早产儿才需要'],
    correctAnswer: '不需要，配方奶中已含足量维生素 D',
    explanation: '市售婴儿配方奶粉均按国家强制标准添加了维生素 D。按标准量喂养的配方奶宝宝（每天约摄入 ≥500ml 配方奶）通常不需要额外补充维生素 D。但如果宝宝每天喝的配方奶量不足 500ml，则应咨询医生是否需要补充。',
  },
  {
    id: 'q-f1-9',
    levelId: 'newborn-feeding-1',
    type: 'scenario',
    text: '宝宝出生第14天，你发现宝宝每次喝奶时特别急，喝得很快——不到5分钟就喝完了90ml，之后频繁吐奶。家人说"喝得快好，省时间"。你会如何调整？',
    options: [
      '继续让宝宝快速喝完，吐奶是正常的',
      '使用低流速奶嘴，控制喂奶节奏，每喝几口暂停一下让宝宝呼吸和吞咽协调',
      '减少奶量到 30ml 一次',
      '改用大孔奶嘴让奶流更快',
    ],
    correctAnswer: '使用低流速奶嘴，控制喂奶节奏，每喝几口暂停一下让宝宝呼吸和吞咽协调',
    explanation: '新生儿吞咽-呼吸协调能力尚未成熟。喝得太快容易吞入空气、加重吐奶和呛奶风险。应选低流速小孔奶嘴，采用"间歇式瓶喂"——让宝宝每吸几口主动暂停换气再继续，整个喂奶过程约15-20分钟为宜。',
  },
  {
    id: 'q-f1-10',
    levelId: 'newborn-feeding-1',
    type: 'truefalse',
    text: '配方奶喂养的宝宝，每次调好的奶喝不完可以放冰箱下次再热了喝。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '配方奶冲调好后应在1小时内喝完。宝宝喝过的奶瓶因混入了口腔细菌，不可留存再喝。未喝完的奶应丢弃。如果出门需要，可以携带干净的温开水和分装好的奶粉，喝前现冲。',
  },
  {
    id: 'q-f1-11',
    levelId: 'newborn-feeding-1',
    type: 'multiple',
    text: '以下哪些是宝宝已经吃饱的可靠信号？',
    options: [
      '吃奶后看起来满足安宁',
      '宝宝主动松开奶嘴、转头、不再对奶瓶感兴趣',
      '每天有足够的湿尿布（6-8片）',
      '每次都把整瓶奶喝完',
    ],
    correctAnswer: ['吃奶后看起来满足安宁', '宝宝主动松开奶嘴、转头、不再对奶瓶感兴趣', '每天有足够的湿尿布（6-8片）'],
    explanation: '吃饱的信号包括：宝宝自动松开奶嘴或转头、吃奶后满足安宁、尿量充足。强迫宝宝必须喝完瓶中的固定量反而可能导致过度喂养。',
  },

  // ==========================================
  // 喂养模块 - 关卡 2 补充（已有7题，+5）
  // ==========================================
  {
    id: 'q-f2-8',
    levelId: 'newborn-feeding-2',
    type: 'truefalse',
    text: '冲好的配方奶在室温下放置超过 2 小时还可以给宝宝喝。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '冲调好的配方奶在室温下应在 1 小时内喝完。超过 1 小时细菌开始繁殖，应丢弃。如果宝宝喝了一部分，剩余的应在 1 小时内处理掉，不可留到下一顿。在冰箱冷藏室（4℃）可保存最多 24 小时。',
  },
  {
    id: 'q-f2-9',
    levelId: 'newborn-feeding-2',
    type: 'single',
    text: '加热冷藏过的配方奶，以下哪种方式是正确的？',
    options: [
      '用微波炉加热 30 秒',
      '放在热水浴中温热（将奶瓶放入热水中），加热后手腕内侧试温',
      '直接用开水烫煮',
      '倒回锅里重新煮沸',
    ],
    correctAnswer: '放在热水浴中温热（将奶瓶放入热水中），加热后手腕内侧试温',
    explanation: '加热配方奶最安全的方式是将奶瓶放在热水浴中慢慢温热，或使用暖奶器。绝对不能用微波炉——微波加热不均匀会产生局部"热点"烫伤宝宝口腔。加热后摇匀并滴在手腕内侧试温，温热不烫即可。',
  },
  {
    id: 'q-f2-10',
    levelId: 'newborn-feeding-2',
    type: 'scenario',
    text: '你准备重返工作岗位，计划白天让家人给宝宝瓶喂配方奶。但宝宝最近在喂奶时总爱玩、不想吃、含着奶嘴不吞咽。家人担心换人喂会更难。你该怎么推进？',
    options: [
      '你亲自继续喂，等宝宝再大些',
      '提前2周开始让家人在宝宝情绪平稳时尝试喂奶，选低流速奶嘴，建立新喂奶人的信任关系',
      '饿宝宝一顿自然就吃了',
      '换流速更快的奶嘴让宝宝尽快喝完',
    ],
    correctAnswer: '提前2周开始让家人在宝宝情绪平稳时尝试喂奶，选低流速奶嘴，建立新喂奶人的信任关系',
    explanation: '宝宝换照护者喂养需要一个适应过程。建议提前1-2周让新照护者在妈妈在家时逐步接手喂奶，选择宝宝情绪好、不太饿也不太困的时候尝试。使用低流速奶嘴给宝宝自己控制的节奏。',
  },
  {
    id: 'q-f2-11',
    levelId: 'newborn-feeding-2',
    type: 'multiple',
    text: '关于配方奶的储存和携带，以下哪些做法是正确的？',
    options: ['冲好后在室温下放置不超过 1 小时', '出门时带干净的温开水 + 分装奶粉，现冲现喝', '喝剩的奶放回冰箱留到下一顿', '冰箱冷藏（4℃）的配方奶最多存放 24 小时'],
    correctAnswer: ['冲好后在室温下放置不超过 1 小时', '出门时带干净的温开水 + 分装奶粉，现冲现喝', '冰箱冷藏（4℃）的配方奶最多存放 24 小时'],
    explanation: '配方奶储存关键三原则：①冲好后 1 小时内喝完，超时丢弃；②宝宝喝过的不可留存（口腔细菌污染）；③出门用"水分装+奶粉分装"方案，现喝现冲最安全。冷藏条件下可存放最多 24 小时。',
  },
  {
    id: 'q-f2-12',
    levelId: 'newborn-feeding-2',
    type: 'single',
    text: '瓶喂时，以下哪种做法是正确的？',
    options: [
      '让宝宝完全平躺喝奶，奶瓶竖直',
      '宝宝保持半竖立姿势，奶瓶倾斜使奶嘴始终充满奶液',
      '奶嘴孔开大一些让宝宝喝得更快',
      '每次都必须喝完瓶里全部奶量',
    ],
    correctAnswer: '宝宝保持半竖立姿势，奶瓶倾斜使奶嘴始终充满奶液',
    explanation: '瓶喂时应让宝宝保持半竖立姿势减少呛奶风险，奶瓶适当倾斜确保奶嘴中充满奶液（而非空气），选择低流速奶嘴让宝宝自己控制节奏。不要强迫喝完瓶内全部奶量——让宝宝自己决定何时吃饱。',
  },
  {
    id: 'q-f2-13',
    levelId: 'newborn-feeding-2',
    type: 'scenario',
    text: '宝宝出生第20天，几乎每次喂奶后都会从嘴角溢出一些奶（湿掉一小块口水巾的量），偶尔在拍嗝时吐出豆腐渣样的消化奶。宝宝体重每天增长约28g，精神状态很好，不哭不闹。婆婆担心是胃有问题，要带去医院做B超。你的判断是？',
    options: [
      '这是典型的胃食管反流病，需要立刻做检查',
      '这是正常的婴儿溢奶（生理性胃食管反流），结合体重增长良好和精神状态好，无需特殊检查',
      '减少每次喂奶量一半，增加喂奶频率',
      '给宝宝喝止吐药',
    ],
    correctAnswer: '这是正常的婴儿溢奶（生理性胃食管反流），结合体重增长良好和精神状态好，无需特殊检查',
    explanation: '新生儿溢奶（每次吐一两口，湿一块小毛巾）非常常见，原因是贲门（食管-胃接口）肌肉松弛。豆腐渣样的奶是奶+胃酸形成的消化奶，不是"变质"。区别溢奶和病理性反流的关键信号是：体重增长正常+精神好+不抗拒喂奶。如果体重停滞、喷射性呕吐、每次喂奶都痛苦哭闹或呕吐物有黄绿色/血丝，才需就医。',
  },

  // ==========================================
  // 喂养模块 - 关卡 3 补充（已有4题，+8）
  // ==========================================
  {
    id: 'q-f3-5',
    levelId: 'newborn-feeding-3',
    type: 'single',
    text: '新生儿出生后第1周，每天尿湿几片尿布才算尿量正常？',
    options: ['第1周没有指标要求', '出生后第4天开始每天至少有5-6片湿尿布', '每天至少10片', '1片就够了'],
    correctAnswer: '出生后第4天开始每天至少有5-6片湿尿布',
    explanation: '出生第1-2天尿量少（1-2片）是正常的。从第4-5天起，随着奶量摄入增多，每天应有至少5-6片沉甸甸的湿尿布。小便颜色应为淡黄色或无色——颜色越深提示摄入越不足。',
  },
  {
    id: 'q-f3-6',
    levelId: 'newborn-feeding-3',
    type: 'scenario',
    text: '宝宝出生第12天，每次吃奶约15-20分钟，但1小时后又开始发出饥饿信号。家人说"奶不够、吃不饱"。你注意到宝宝每天有7片湿尿布，大便金黄软便。你的判断是？',
    options: [
      '确实奶不够，立即加配方奶',
      '频繁进食可能是"密集进食期"的正常表现，只要尿布充足、体重在增长就无需过度担心',
      '让宝宝哭一会儿拉长间隔',
      '给宝宝喂水补充',
    ],
    correctAnswer: '频繁进食可能是"密集进食期"的正常表现，只要尿布充足、体重在增长就无需过度担心',
    explanation: '出生后1-3周可能出现"密集进食期"（Cluster Feeding）——宝宝在几小时内多次想要吃奶。这不是奶不够的信号，而是宝宝在建立奶量的正常行为。可靠判断标准是尿布数量（7片达标）和体重增长趋势。',
  },
  {
    id: 'q-f3-7',
    levelId: 'newborn-feeding-3',
    type: 'truefalse',
    text: '配方奶喂养的宝宝应该严格按照奶粉罐上的建议奶量表来喂，不能多也不能少。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '奶粉罐上的建议量只是参考，不是必须严格执行的标准。每个宝宝的需求不同，同一宝宝不同时间的食量也不同。应观察宝宝的饥饿和饱足信号，灵活调整。如果宝宝在喝完瓶中奶后还表现出饥饿，可以多准备一些。',
  },
  {
    id: 'q-f3-8',
    levelId: 'newborn-feeding-3',
    type: 'single',
    text: '冲泡配方奶时，正确的操作是？',
    options: [
      '先放奶粉再加水，这样浓度更准确',
      '先放需要量的水，再加入精确计量的奶粉',
      '水加多少无所谓，浓一点有营养',
      '用矿泉水冲泡',
    ],
    correctAnswer: '先放需要量的水，再加入精确计量的奶粉',
    explanation: '正确顺序：先加入准确计量的水，再加入平勺奶粉，搅拌均匀。先放奶粉后加水会导致水量不准。配方奶过浓会增加宝宝肾脏负担、过稀会导致营养不良。用干净的自来水烧开后降温到合适温度即可，无需矿泉水。',
  },
  {
    id: 'q-f3-9',
    levelId: 'newborn-feeding-3',
    type: 'scenario',
    text: '你给宝宝冲了120ml配方奶，宝宝喝了80ml就睡着了。剩下的40ml奶放在桌上，2小时后宝宝醒了又哭着要吃。剩下的奶该怎么处理？',
    options: [
      '直接给宝宝喝，2小时没关系',
      '加热到沸腾再给宝宝喝',
      '扔掉，重新冲一瓶新的',
      '放冰箱下次再喝',
    ],
    correctAnswer: '扔掉，重新冲一瓶新的',
    explanation: '配方奶冲调后在室温下 1 小时内应喝完，超过 1 小时细菌开始繁殖应丢弃。宝宝喝过的奶更不可留——口腔细菌已进入奶液。每次重新冲调新鲜配方奶，保证宝宝喝到的安全。',
  },
  {
    id: 'q-f3-10',
    levelId: 'newborn-feeding-3',
    type: 'multiple',
    text: '配方奶喂养的宝宝出现以下哪些情况，需要警惕可能对当前配方奶不耐受或过敏，应考虑就医评估？',
    options: [
      '喂奶后频繁哭闹、弓背、严重吐奶',
      '大便带血丝或黏液',
      '全身反复出现湿疹或皮疹，常规护理不缓解',
      '每次都能喝完一整瓶奶',
    ],
    correctAnswer: ['喂奶后频繁哭闹、弓背、严重吐奶', '大便带血丝或黏液', '全身反复出现湿疹或皮疹，常规护理不缓解'],
    explanation: '牛奶蛋白过敏（CMPA）是配方奶喂养宝宝最常见的食物过敏之一。警示信号包括：①消化道症状——频繁吐奶、哭闹弓背、腹泻便秘交替、大便带血丝/黏液；②皮肤症状——反复湿疹、荨麻疹；③呼吸道症状——喘息、慢性鼻塞。如有上述表现，应及时就医，医生可能建议换用水解蛋白配方或氨基酸配方。注意：轻度溢奶和偶尔哭闹是正常的，不要盲目换奶。',
  },
  {
    id: 'q-f3-11',
    levelId: 'newborn-feeding-3',
    type: 'truefalse',
    text: '配方奶喂养的新生儿在出生后几天内需要额外喂水，以防上火。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '6个月以内的宝宝不管是母乳还是配方奶喂养，一般都不需要额外喝水。配方奶中近90%是水份，按正确比例冲调的奶足以满足宝宝的水份需求。给新生儿额外喂水会占据胃容量、减少奶量摄入，可能影响体重增长。',
  },
  {
    id: 'q-f3-12',
    levelId: 'newborn-feeding-3',
    type: 'single',
    text: '配方奶喂养时，以下哪种做法是正确的拍嗝策略？',
    options: [
      '每喂 30-60ml 就暂停一次拍嗝，喂完后继续拍',
      '只有喂完一整瓶后才拍一次',
      '拍出嗝就不需要再拍了',
      '拍嗝时让宝宝趴在腿上',
    ],
    correctAnswer: '每喂 30-60ml 就暂停一次拍嗝，喂完后继续拍',
    explanation: '瓶喂时建议每喝 30-60ml 暂停一次拍嗝（将宝宝竖直抱起，一手托住下巴和前胸，另一手空掌由下向上轻拍背），喂完后再继续拍几分钟。这能有效减少胃内空气积聚，降低吐奶和胀气发生。',
  },

  // ==========================================
  // 睡眠模块 - 关卡 1 补充（已有4题，+8）
  // ==========================================
  {
    id: 'q-s1-5',
    levelId: 'newborn-sleep-1',
    type: 'truefalse',
    text: '使用安抚奶嘴可以降低 SIDS 风险，因此在宝宝睡着后如果奶嘴掉出来，应该把它重新放回去。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '研究发现使用安抚奶嘴确实与降低SIDS风险相关，但应在宝宝入睡时提供即可。如果奶嘴在宝宝睡着后掉出来，不需要再放回去。如果宝宝拒绝奶嘴，也不要强迫。建议等宝宝喂养规律基本稳定后（约3-4周）再引入安抚奶嘴，避免干扰正常喂养信号。',
  },
  {
    id: 'q-s1-6',
    levelId: 'newborn-sleep-1',
    type: 'single',
    text: 'AAP 建议婴儿与父母同室但不同床至少到多大的月龄？',
    options: ['满月', '3个月前', '6个月（理想是1年）', '1岁以后'],
    correctAnswer: '6个月（理想是1年）',
    explanation: 'AAP推荐婴儿与父母同室但不同床至少到6个月，理想情况延续到1年。同室睡眠可将SIDS风险降低最多50%。婴儿应睡在父母床边独立的、符合安全标准的婴儿床或摇篮中。',
  },
  {
    id: 'q-s1-7',
    levelId: 'newborn-sleep-1',
    type: 'scenario',
    text: '你在为宝宝准备新生儿睡篮。需要在里面放些什么？',
    options: [
      '床垫铺紧绷床单、可穿式睡袋，不放置枕头、毛绒玩具、防撞护垫',
      '床垫+床单+小枕头+薄被子+安抚玩具',
      '床垫+床单+防撞护垫+毛毯',
      '放什么都无所谓，宝宝舒服最重要',
    ],
    correctAnswer: '床垫铺紧绷床单、可穿式睡袋，不放置枕头、毛绒玩具、防撞护垫',
    explanation: '安全的婴儿睡眠环境遵循"bare is best"原则：坚硬的床垫+紧绷床单+宝宝+睡袋，仅此而已。枕头、被子、毛绒玩具、防撞护垫都是潜在的窒息和捂闷风险。',
  },
  {
    id: 'q-s1-8',
    levelId: 'newborn-sleep-1',
    type: 'multiple',
    text: '以下哪些因素可以降低婴儿猝死综合征（SIDS）的风险？',
    options: [
      '婴儿仰卧入睡',
      '使用坚硬的床垫',
      '避免孕期和产后吸烟',
      '同室不同床',
      '给婴儿盖厚被保暖',
    ],
    correctAnswer: ['婴儿仰卧入睡', '使用坚硬的床垫', '避免孕期和产后吸烟', '同室不同床'],
    explanation: '降低SIDS风险的有效措施包括：仰卧入睡、坚硬的床垫、同室不同床、避免吸烟和二手烟、母乳喂养、使用安抚奶嘴（入睡时）、避免过热（室温20-22℃为宜）。加厚被子反而增加风险。',
  },
  {
    id: 'q-s1-9',
    levelId: 'newborn-sleep-1',
    type: 'single',
    text: '新生儿睡眠的适宜室温是？',
    options: ['18℃-20℃', '20℃-22℃', '25℃-28℃', '越暖越好'],
    correctAnswer: '20℃-22℃',
    explanation: '婴儿睡眠的适宜室温约为20-22℃。过热是SIDS的危险因素之一。判断宝宝是否穿够了，不要靠摸手脚（新生儿手脚冷是正常的），而是摸后颈或胸口——温热无汗就合适。',
  },
  {
    id: 'q-s1-10',
    levelId: 'newborn-sleep-1',
    type: 'truefalse',
    text: '襁褓（包裹）应该从肩部开始紧紧包裹到双腿，让宝宝的腿不能弯曲。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '襁褓应从肩部包裹到躯干，但下半部分应宽松，让宝宝的腿可以自由弯曲和外展——紧裹双腿可能增加髋关节发育不良的风险。当宝宝出现翻身迹象时（约2-3个月），应停止使用襁褓。',
  },
  {
    id: 'q-s1-11',
    levelId: 'newborn-sleep-1',
    type: 'scenario',
    text: '宝宝出生第8天，在婴儿床里睡得很安稳。奶奶走进去发现宝宝仰卧着，想把宝宝翻成侧卧"这样头型好看"。你该怎么处理？',
    options: [
      '听从奶奶的建议改成侧卧',
      '坚持仰卧，并向奶奶解释这是降低SIDS风险最重要的措施；头型问题可以通过醒着时多趴（Tummy Time）来改善',
      '改成俯卧更好，宝宝睡得更沉',
      '无所谓，哪种姿势都行',
    ],
    correctAnswer: '坚持仰卧，并向奶奶解释这是降低SIDS风险最重要的措施；头型问题可以通过醒着时多趴（Tummy Time）来改善',
    explanation: '仰卧入睡是目前公认降低SIDS风险最重要的措施。担心头型变扁可以在宝宝醒着且有成人监督时多进行俯趴练习（Tummy Time），也可以改变抱的方向和宝宝看的方向。',
  },
  {
    id: 'q-s1-12',
    levelId: 'newborn-sleep-1',
    type: 'single',
    text: '关于婴儿床放置位置，以下哪个是最安全的？',
    options: [
      '紧靠窗户，光线好',
      '远离窗户、窗帘拉绳和任何可能掉落的物品，放在父母床旁',
      '放在暖气旁边，冬天温暖',
      '放在书架下面，节省空间',
    ],
    correctAnswer: '远离窗户、窗帘拉绳和任何可能掉落的物品，放在父母床旁',
    explanation: '婴儿床应远离窗户（防跌落和窗帘绳窒息）、暖气或空调出风口、任何可能掉落的画框或搁架。窗帘拉绳等带状物应完全收纳到宝宝触碰不到的位置。',
  },

  // ==========================================
  // 睡眠模块 - 关卡 2 补充（已有4题，+8）
  // ==========================================
  {
    id: 'q-s2-5',
    levelId: 'newborn-sleep-2',
    type: 'truefalse',
    text: '新生儿睡觉时发出哼哼声、扭动、甚至短暂睁眼，说明他醒了需要立刻抱起安抚。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '新生儿在睡眠中发出声音、扭动、甚至短暂睁眼后继续入睡，通常是处于"活跃睡眠期"（浅睡眠）的正常表现。这时如果立刻抱起反而可能真正弄醒他。可以在旁观察片刻——通常宝宝会自己过渡到下一个睡眠周期。',
  },
  {
    id: 'q-s2-6',
    levelId: 'newborn-sleep-2',
    type: 'single',
    text: '新生儿一个完整的睡眠周期大约是多久？',
    options: ['20-30分钟', '50-60分钟', '90-120分钟', '4-6小时'],
    correctAnswer: '50-60分钟',
    explanation: '新生儿睡眠周期约50-60分钟，远短于成人约90分钟的周期。且新生儿浅睡眠（REM）占比约50%以上（成人只有约20%），这就是他们容易醒来的生理基础——不是"坏习惯"，是正常发育过程。',
  },
  {
    id: 'q-s2-7',
    levelId: 'newborn-sleep-2',
    type: 'scenario',
    text: '宝宝2个月大，每天晚上7-8点开始哭闹，怎么哄都不行，持续几个小时直到深夜才安静下来。喂奶、换尿布、抱着走都试了效果一般。宝宝白天吃奶正常，精神也好。你的判断是？',
    options: [
      '宝宝一定生病了，马上去急诊',
      '这可能是典型的"黄昏闹"（密集哭闹期），通常发生在傍晚至夜间，是正常发育阶段，一般在3-4个月自然缓解',
      '一定是没吃饱，大量加配方奶',
      '白天让宝宝多睡，晚上自然就不闹了',
    ],
    correctAnswer: '这可能是典型的"黄昏闹"（密集哭闹期），通常发生在傍晚至夜间，是正常发育阶段，一般在3-4个月自然缓解',
    explanation: '新生儿期常见的"黄昏闹"（也称"紫色哭闹期"PURPLE Crying），通常在2-3周开始，6-8周达到高峰，3-4个月自然缓解。特征是每天固定时段（多在傍晚/夜间）不可安抚的哭闹，但宝宝其他时间健康正常。应对方法：确认基本需求满足后，轮流安抚、适当白噪音、抱着走等。如果伴随发热、呕吐、吃奶明显减少，需就医排除其他问题。',
  },
  {
    id: 'q-s2-8',
    levelId: 'newborn-sleep-2',
    type: 'multiple',
    text: '以下哪些做法有助于帮助宝宝建立健康的睡眠习惯？',
    options: [
      '在宝宝还醒着但表现出困倦信号时放到床上，而不是等完全睡着再放',
      '确保宝宝每次睡觉的时间、地点、方式完全一致，不能有任何变化',
      '白天小睡不要拉上全遮光窗帘（保持正常光线），晚上保持昏暗安静',
      '给宝宝建立简短、一致的睡前程序',
    ],
    correctAnswer: ['在宝宝还醒着但表现出困倦信号时放到床上，而不是等完全睡着再放', '白天小睡不要拉上全遮光窗帘（保持正常光线），晚上保持昏暗安静', '给宝宝建立简短、一致的睡前程序'],
    explanation: '健康的睡眠习惯培养包括：捕捉困倦信号（揉眼睛、打哈欠、烦躁）及时安排小睡；在宝宝仍然醒着但困倦时放下（帮助学会自我安抚入睡）；白天保持正常光线与活动，晚上昏暗安静以建立昼夜节律；建立简短的睡前程序。但不要追求每次完全一致——适度的灵活性也很重要。',
  },
  {
    id: 'q-s2-9',
    levelId: 'newborn-sleep-2',
    type: 'truefalse',
    text: '如果宝宝夜里不吃奶就不会睡好，所以晚上必须每2小时强制叫醒喂奶。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '一旦宝宝恢复了出生体重并持续按预期增长（通常在出生后2周左右），就不需要夜间定时叫醒喂奶了。让宝宝按自己的节奏醒来自主要求喂养。但如果医生因早产或体重增长问题建议定时唤醒，则遵医嘱。',
  },
  {
    id: 'q-s2-10',
    levelId: 'newborn-sleep-2',
    type: 'single',
    text: '以下哪种安抚方式对新生儿比较安全？',
    options: [
      '抱着宝宝边走边摇，但幅度较大',
      '轻柔有节奏的动作，如慢慢走动、轻轻摇摆',
      '把宝宝放在振动剧烈的摇椅上',
      '让宝宝趴在你胸口一起在沙发睡着',
    ],
    correctAnswer: '轻柔有节奏的动作，如慢慢走动、轻轻摇摆',
    explanation: '新生儿喜欢轻柔有节奏的动作——在子宫里他习惯了这种体验。慢慢走动、轻轻摇晃都是安全的安抚方法。但剧烈摇晃非常危险，沙发同睡有窒息风险。',
  },
  {
    id: 'q-s2-11',
    levelId: 'newborn-sleep-2',
    type: 'scenario',
    text: '深夜2点，宝宝哭闹。你已经喂了奶、换了尿布、检查了温度、抱了20分钟。你感到极度疲惫和沮丧，甚至有点想用力摇晃宝宝。你应该怎么做？',
    options: [
      '把宝宝安全地放在婴儿床里，自己离开房间几分钟冷静，深呼吸，等情绪平复后再回去',
      '继续抱着宝宝，摇晃是唯一能让他停下来的方法',
      '趁情绪激动时用力摇晃宝宝，让他停止哭闹',
      '给宝宝喂一点镇静药物',
    ],
    correctAnswer: '把宝宝安全地放在婴儿床里，自己离开房间几分钟冷静，深呼吸，等情绪平复后再回去',
    explanation: '当你感到情绪即将失控时，把宝宝安全放在婴儿床中，自己走开到另一个房间几分钟，深呼吸、洗个脸、喝杯水。在这几分钟内宝宝哭不会有事，但你在愤怒中摇晃宝宝可能造成不可逆的脑损伤。所有照护者都应了解并接受这个安全策略。',
  },
  {
    id: 'q-s2-12',
    levelId: 'newborn-sleep-2',
    type: 'single',
    text: '新生儿睡眠中以下哪种表现需要就医？',
    options: [
      '睡觉时有呼吸暂停超过20秒，或伴有皮肤发青、苍白',
      '睡觉时发出轻微的呼噜声',
      '睡觉时偶尔抽动手脚',
      '浅睡眠时眼球在眼皮下转动',
    ],
    correctAnswer: '睡觉时有呼吸暂停超过20秒，或伴有皮肤发青、苍白',
    explanation: '新生儿不规律的呼吸模式（周期性呼吸）是正常的——比如呼吸十几秒后暂停5-10秒再继续。但如果呼吸暂停持续超过20秒，或伴有口唇面色发青（紫绀）、变得苍白无力，则是需要紧急就医的信号。',
  },

  // ==========================================
  // 睡眠模块 - 关卡 3 补充（已有3题，+9）
  // ==========================================
  {
    id: 'q-s3-4',
    levelId: 'newborn-sleep-3',
    type: 'single',
    text: '新生儿通常到多大月龄开始形成较为明显的昼夜节律？',
    options: ['出生时就有', '1-2周', '3-4个月', '6个月以后'],
    correctAnswer: '3-4个月',
    explanation: '新生儿的昼夜节律尚未成熟，这与大脑中松果体分泌褪黑素的机制尚未完善有关。通常到3-4个月左右，宝宝才开始形成明显的昼夜节律，夜间连续睡眠时间逐渐延长。在这之前，照护者的引导尤为重要。',
  },
  {
    id: 'q-s3-5',
    levelId: 'newborn-sleep-3',
    type: 'scenario',
    text: '你发现宝宝白天每次小睡都很短（20-30分钟就醒），奶奶建议白天把窗帘全部拉上、保持绝对安静来延长小睡。你的做法？',
    options: [
      '完全照奶奶说的做',
      '白天小睡不必全遮光和绝对安静，保持正常生活光线和些许环境音即可——这样有助于帮宝宝区分白天小睡和夜间长睡',
      '让宝宝白天别睡了，晚上就能睡久',
      '白天开着电视增加背景音帮助入睡',
    ],
    correctAnswer: '白天小睡不必全遮光和绝对安静，保持正常生活光线和些许环境音即可——这样有助于帮宝宝区分白天小睡和夜间长睡',
    explanation: '白天小睡不需要像夜间那样全黑和绝对安静——保持正常的生活光线和些许环境音，更有助于帮宝宝区分"这是白天的小睡"和"这是晚上的长睡眠"。这是帮助建立昼夜节律的重要策略。',
  },
  {
    id: 'q-s3-6',
    levelId: 'newborn-sleep-3',
    type: 'multiple',
    text: '以下哪些属于一个好的新生儿睡前程序可以包含的要素？',
    options: [
      '温水洗澡',
      '轻柔抚触按摩',
      '换上睡衣和干净尿布',
      '大声播放音乐或开着电视',
      '把灯光调暗，低声说话或唱摇篮曲',
    ],
    correctAnswer: ['温水洗澡', '轻柔抚触按摩', '换上睡衣和干净尿布', '把灯光调暗，低声说话或唱摇篮曲'],
    explanation: '帮助入睡的睡前程序应包含安抚性、低刺激的活动：温水澡、抚触按摩、换睡衣、调暗灯光、低声唱歌或讲故事。大声喧哗、电视屏幕等强刺激反而会让宝宝更难安静下来。',
  },
  {
    id: 'q-s3-7',
    levelId: 'newborn-sleep-3',
    type: 'truefalse',
    text: '给新生儿看手机或平板上的动画有助于培养专注力。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: 'AAP明确建议：18个月以下的婴儿应完全避免使用屏幕（视频通话除外）。屏幕的光线和快速画面会过度刺激新生儿大脑，干扰睡眠和发育，且没有任何证据表明它对培养专注力有益。',
  },
  {
    id: 'q-s3-8',
    levelId: 'newborn-sleep-3',
    type: 'single',
    text: '宝宝白天醒着时，以下哪种活动最重要？',
    options: [
      '让宝宝躺在婴儿车里看风景',
      '在成人监督下进行俯趴练习（Tummy Time），促进颈部和上半身力量',
      '让宝宝多看手机动画',
      '一直抱着不让宝宝动',
    ],
    correctAnswer: '在成人监督下进行俯趴练习（Tummy Time），促进颈部和上半身力量',
    explanation: 'Tummy Time（俯趴练习）是新生儿白天最重要的活动之一。在宝宝醒着且有成人监督时，每天短时多次地进行俯趴，可以锻炼颈部、肩部和上半身力量，预防头型变扁，为后续抬头、翻身等大运动发育打下基础。',
  },
  {
    id: 'q-s3-9',
    levelId: 'newborn-sleep-3',
    type: 'truefalse',
    text: '为了延长夜间睡眠，白天应该尽量让新生儿保持清醒、减少小睡。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '让新生儿过度疲劳反而会使他们更难入睡、更容易惊醒。"睡得越多，睡得越好"在婴儿中是正确的——白天有规律的小睡有助于夜间睡眠质量。过度疲劳的宝宝体内会释放皮质醇（应激激素），反而导致入睡困难和频繁夜醒。',
  },
  {
    id: 'q-s3-10',
    levelId: 'newborn-sleep-3',
    type: 'scenario',
    text: '宝宝出生3周，晚上9点喂完奶后你在昏暗灯光下给他换了尿布、穿上睡袋，轻轻唱了摇篮曲后放到婴儿床里。但宝宝睁着眼睛10分钟还没睡着，开始小声哼唧。你该怎么做？',
    options: [
      '立即抱起来重新安抚，直到宝宝完全熟睡',
      '在旁安静陪伴片刻，允许宝宝在有困意时学习自己入睡——哼唧不一定是需要救的信号',
      '打开灯陪宝宝玩一会儿',
      '让宝宝哭到睡着为止',
    ],
    correctAnswer: '在旁安静陪伴片刻，允许宝宝在有困意时学习自己入睡——哼唧不一定是需要救的信号',
    explanation: '在宝宝仍然清醒但表现出困倦时放到床上，这为宝宝提供了学习自己入睡的机会。轻微的哼唧可能只是宝宝在自我安抚过程中的短暂表现，可以在旁边安静陪伴片刻观察。如果哭闹升级，再考虑安抚。不需要每次都抱着哄到完全熟睡。',
  },
  {
    id: 'q-s3-11',
    levelId: 'newborn-sleep-3',
    type: 'single',
    text: '关于新生儿白噪音的使用，以下哪种说法是正确的？',
    options: [
      '白噪音应该开得越响越好，盖过所有环境音',
      '白噪音可以开到最大音量，因为不会伤害新生儿听力',
      '白噪音音量不应超过50分贝（约淋浴声大小），播放器应放在离婴儿床至少2米远',
      '白噪音必须整晚开着，不能停',
    ],
    correctAnswer: '白噪音音量不应超过50分贝（约淋浴声大小），播放器应放在离婴儿床至少2米远',
    explanation: '白噪音对部分新生儿有安抚作用（模拟子宫内的声音环境），但使用时需注意：音量不应超过50分贝、播放器应远离婴儿床至少2米、可以设定时间自动关闭。过高音量长时间播放可能损害婴儿听力。',
  },
  {
    id: 'q-s3-12',
    levelId: 'newborn-sleep-3',
    type: 'multiple',
    text: '以下哪些表现说明宝宝的昼夜节律开始建立？（通常发生在3-4个月）',
    options: [
      '夜间连续睡眠时间逐渐延长',
      '白天清醒时间变长、互动增多',
      '在固定时间表现出困倦',
      '出生第2天就一觉睡到天亮',
    ],
    correctAnswer: ['夜间连续睡眠时间逐渐延长', '白天清醒时间变长、互动增多', '在固定时间表现出困倦'],
    explanation: '昼夜节律建立的标志包括：夜间睡眠时间延长、白天清醒互动增多、入睡时间和醒来时间逐渐规律。出生前几天就"睡整觉"的新生儿反而可能是嗜睡的警示信号（尤其是黄疸未消退时），需要密切关注喂养和排泄。',
  },

  // ==========================================
  // 日常护理模块 - 关卡 1 补充（已有5题，+7）
  // ==========================================
  {
    id: 'q-c1-6',
    levelId: 'newborn-care-1',
    type: 'truefalse',
    text: '脐带脱落时如果出血超过几滴或持续出血不止，属于正常现象，擦干净即可。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '脐带脱落过程中少量几滴血迹是正常的。但如果出血持续不止、需要用纱布按压才能止住、或脐周红肿、有脓性分泌物，这些都是需要就医的信号。脐带脱落处少量清澈或淡黄渗出液正常，但黄色脓性、有异味的分泌物是感染信号。',
  },
  {
    id: 'q-c1-7',
    levelId: 'newborn-care-1',
    type: 'single',
    text: '脐带脱落超过多长时间仍未脱落应咨询医生？',
    options: ['1周', '3周', '6周', '2个月'],
    correctAnswer: '3周',
    explanation: '大部分脐带残端在出生后1-3周内脱落。如果3周后仍未脱落（但脐周干净无感染迹象），应咨询医生。脐带持续不脱落有时可能是脐肉芽肿或轻微免疫问题的信号。',
  },
  {
    id: 'q-c1-8',
    levelId: 'newborn-care-1',
    type: 'scenario',
    text: '宝宝出生第12天，你今天换尿布时发现他的屁股、大腿根部皮肤发红，还有一些细小的疹子，宝宝换尿布时哭闹得比平时厉害。你该怎么护理？',
    options: [
      '这是尿布疹，每次换尿布时用温水和软布轻柔清洗、轻拍干爽后涂上含氧化锌的护臀膏，每天让宝宝光着屁股趴一会儿',
      '用含酒精的湿巾用力擦拭发红区域杀菌',
      '涂上厚厚的爽身粉让皮肤保持干爽',
      '这是正常现象不需要处理，继续正常换尿布即可',
    ],
    correctAnswer: '这是尿布疹，每次换尿布时用温水和软布轻柔清洗、轻拍干爽后涂上含氧化锌的护臀膏，每天让宝宝光着屁股趴一会儿',
    explanation: '尿布疹是新生儿最常见的皮肤问题之一，几乎每个宝宝都会遇到。核心护理原则：保持清洁干爽。每次换尿布时用温水轻柔清洗（避免含酒精/香料的湿巾），轻轻拍干而非擦拭，涂上含氧化锌的护臀膏隔离刺激物。每天让宝宝不穿尿布趴一会儿，让皮肤接触空气。不要使用爽身粉——粉尘会被宝宝吸入刺激肺部。通常仔细护理3-4天会明显好转。',
  },
  {
    id: 'q-c1-9',
    levelId: 'newborn-care-1',
    type: 'multiple',
    text: '以下哪些是关于新生儿指甲护理的正确做法？',
    options: [
      '使用婴儿专用指甲剪或指甲锉修剪指甲',
      '在宝宝熟睡时修剪指甲更容易操作',
      '用牙齿咬掉宝宝的指甲即可',
      '修剪后用手指摸一下边缘确保没有尖锐的角',
    ],
    correctAnswer: ['使用婴儿专用指甲剪或指甲锉修剪指甲', '在宝宝熟睡时修剪指甲更容易操作', '修剪后用手指摸一下边缘确保没有尖锐的角'],
    explanation: '新生儿指甲生长很快，指甲过长会抓伤面部。建议使用婴儿专用指甲剪或指甲锉，在宝宝熟睡时操作更容易。修剪后摸一下边缘确保圆滑。不要用牙齿咬——口腔细菌可能感染宝宝手指。',
  },
  {
    id: 'q-c1-10',
    levelId: 'newborn-care-1',
    type: 'single',
    text: '脐带脱落后，脐窝处出现一个红色湿润的小"肉疙瘩"（绿豆大小），有少量淡黄色液体渗出。这最可能是什么？',
    options: [
      '脐带癌变，需要紧急手术',
      '脐肉芽肿——一小块多余的疤痕组织，通常需要医生用硝酸银处理或简单结扎',
      '正常的愈合过程，多擦酒精就会消失',
      '一定是脐疝',
    ],
    correctAnswer: '脐肉芽肿——一小块多余的疤痕组织，通常需要医生用硝酸银处理或简单结扎',
    explanation: '脐带脱落后偶尔会形成脐肉芽肿——一小块亮的红色湿润的"肉疙瘩"。它不是感染也不是肿瘤，而是愈合过程中形成的多余肉芽组织。不用自行处理，应由医生评估——通常用硝酸银烧灼或简单结扎即可解决。',
  },
  {
    id: 'q-c1-11',
    levelId: 'newborn-care-1',
    type: 'truefalse',
    text: '脐疝（肚脐鼓出来的软包块）在新生儿中很常见，大多数在1岁内会自行闭合。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '脐疝在新生儿中很常见，尤其是早产儿和非洲裔婴儿。它是由于腹部肌肉尚未完全闭合所致，宝宝哭闹或用力时更明显。绝大多数脐疝在1岁内会自行闭合。不需要压硬币或绑腹带——这些民间做法无效且可能有害。',
  },
  {
    id: 'q-c1-12',
    levelId: 'newborn-care-1',
    type: 'single',
    text: '清洁男宝宝的阴茎，正确的方式是？',
    options: [
      '每次洗澡时用力将包皮向后推开清洗',
      '只需像清洗手指一样清洗阴茎外部，不要强行后推包皮',
      '用棉签伸入包皮内清洁',
      '经常用酒精擦拭包皮',
    ],
    correctAnswer: '只需像清洗手指一样清洗阴茎外部，不要强行后推包皮',
    explanation: '清洁男婴的阴茎时只需清洗外部，不要强行将包皮向后推——这会造成疼痛和撕裂。包皮在婴幼儿期与龟头是自然粘连的，会随着年龄逐渐自然分离，强行后推反而可能导致疤痕和粘连。通常在青春期前后包皮才能轻松翻开。',
  },
  {
    id: 'q-c1-13',
    levelId: 'newborn-care-1',
    type: 'scenario',
    text: '宝宝出生第8天，脐带还没有脱落。你正准备给宝宝擦浴，婆婆说"脐带没掉不能沾水，一个月不能洗澡"。而育儿百科上说可以擦浴。此时你的正确做法是？',
    options: [
      '听婆婆的，一个月内完全不洗',
      '可以进行擦浴（用湿毛巾擦拭身体），但确保脐带区域保持干燥不被浸湿',
      '把宝宝放进澡盆全身泡澡，脐带泡水也没关系',
      '用保鲜膜把脐带紧紧包住然后下水洗澡',
    ],
    correctAnswer: '可以进行擦浴（用湿毛巾擦拭身体），但确保脐带区域保持干燥不被浸湿',
    explanation: '脐带未脱落期间可以进行擦浴——用温湿毛巾从干净区域到脏区依次擦拭，避开脐带让脐部保持干燥。不建议全身浸入水中泡澡，因为脐带残端浸泡后会延缓脱落并增加感染风险。可以用防水护脐贴辅助，但更推荐直接擦浴。等脐带完全脱落后，再转为盆浴。',
  },

  // ==========================================
  // 日常护理模块 - 关卡 2 补充（已有5题，+7）
  // ==========================================
  {
    id: 'q-c2-6',
    levelId: 'newborn-care-2',
    type: 'single',
    text: '擦浴时正确的清洗顺序是？',
    options: [
      '先洗尿布区域再洗脸',
      '从最干净的区域开始：眼睛→面部→身体→尿布区域最后',
      '全身同时洗，顺序无所谓',
      '先洗屁股再洗头',
    ],
    correctAnswer: '从最干净的区域开始：眼睛→面部→身体→尿布区域最后',
    explanation: '擦浴顺序应从最干净的部位开始：先清洁眼睛（从内眼角向外眼角）、面部、身体，最后才是尿布区域。这样可以防止将尿布区域的细菌带到面部或身体其他部位。',
  },
  {
    id: 'q-c2-7',
    levelId: 'newborn-care-2',
    type: 'truefalse',
    text: '新生儿头上出现黄色厚痂（乳痂/摇篮帽），应该用手或硬梳子刮掉。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '乳痂（摇篮帽）是皮脂腺分泌过度所致，表现为头皮上黄色油腻的鳞屑或厚痂，不会引起不适。不要用手指抠或用硬梳子刮——可能损伤头皮引起感染。正确方法：洗头前在头皮上涂抹矿物油或婴儿油软化痂皮（15-30分钟），然后用软刷或湿毛巾打圈轻轻去除。',
  },
  {
    id: 'q-c2-8',
    levelId: 'newborn-care-2',
    type: 'scenario',
    text: '宝宝在盆浴时突然拉大便了。你此时该怎么处理？',
    options: [
      '继续洗，大便也是"水"',
      '立即把宝宝从脏水里抱出来，清空浴盆、清洁消毒后再重新放水',
      '把脏水放一部分加新水就行',
      '用浴液多洗几遍就干净了',
    ],
    correctAnswer: '立即把宝宝从脏水里抱出来，清空浴盆、清洁消毒后再重新放水',
    explanation: '盆浴中如果宝宝排便，应立即抱出宝宝，将脏水完全排空，清洁浴盆后再重新放水。粪便中的细菌可能通过尿道引起尿路感染，不能用同一盆水继续洗。',
  },
  {
    id: 'q-c2-9',
    levelId: 'newborn-care-2',
    type: 'multiple',
    text: '给新生儿选择沐浴和护肤产品时，正确的是？',
    options: [
      '选择无香料、无酒精、低致敏的婴儿专用产品',
      '沐浴液每周使用不超过3次，其余时间清水洗即可',
      '洗完澡后趁皮肤还湿润时涂抹无香料润肤乳锁住水分',
      '成人使用的产品只要"温和"就可以给新生儿用',
    ],
    correctAnswer: ['选择无香料、无酒精、低致敏的婴儿专用产品', '沐浴液每周使用不超过3次，其余时间清水洗即可', '洗完澡后趁皮肤还湿润时涂抹无香料润肤乳锁住水分'],
    explanation: '新生儿皮肤极其娇嫩：应选无香料、无酒精的婴儿产品；每周不超过3次沐浴液；洗后立即涂抹润肤乳锁水。新生儿衣服在首次穿前应单独至少漂洗两次，去除残留的化学物质。成人产品即使标"温和"，也可能含有对婴儿有害的成分。',
  },
  {
    id: 'q-c2-10',
    levelId: 'newborn-care-2',
    type: 'truefalse',
    text: '用棉签伸入新生儿耳道清洁耳垢是保持耳朵卫生的好方法。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '绝对不要用棉签或任何物品伸入新生儿耳道——可能损伤耳道或鼓膜，反而将耳垢推得更深。耳垢会自然向外排出。只需用湿毛巾清洁外耳廓即可。',
  },
  {
    id: 'q-c2-11',
    levelId: 'newborn-care-2',
    type: 'single',
    text: '带新生儿从外面寒冷环境回到温暖的室内后，以下哪种做法是正确的？',
    options: [
      '立即给宝宝洗热水澡回暖',
      '脱掉外层衣物，让宝宝逐渐适应室温，用手摸后颈部判断宝宝体温是否合适',
      '把室内暖气调到最高温度',
      '用热水袋敷在宝宝手脚上',
    ],
    correctAnswer: '脱掉外层衣物，让宝宝逐渐适应室温，用手摸后颈部判断宝宝体温是否合适',
    explanation: '从寒冷环境进入温暖室内，应脱掉最外层衣物让宝宝逐渐适应。判断冷热不应靠摸手脚（新生儿手脚偏冷是正常的），而应摸后颈或胸口——温热无汗就是合适的温度。切记不要在婴儿床上放置热水袋或电热毯。',
  },
  {
    id: 'q-c2-12',
    levelId: 'newborn-care-2',
    type: 'scenario',
    text: '宝宝的面部、颈部和前胸出现一片红色小疹子，但宝宝似乎不痒不痛，精神吃奶正常。你判断这最可能是什么？',
    options: [
      '严重过敏，立即去急诊',
      '可能是新生儿痤疮或热疹，通常无需特殊治疗，保持凉快干爽、观察即可',
      '一定是湿疹，需要激素药膏',
      '这很危险，需要吃抗生素',
    ],
    correctAnswer: '可能是新生儿痤疮或热疹，通常无需特殊治疗，保持凉快干爽、观察即可',
    explanation: '新生儿期面部、颈部的红色小疹子最常见的原因是新生儿痤疮（受母体激素影响，不需治疗，几个月消退）或热疹（穿太多/室温太高所致，保持凉快干爽即可）。如果皮疹持续加重、有水疱、或伴有发热、精神差，再就医。',
  },

  // ==========================================
  // 日常护理模块 - 关卡 3 补充（已有5题，+7）
  // ==========================================
  {
    id: 'q-c3-6',
    levelId: 'newborn-care-3',
    type: 'single',
    text: '给女婴清洁臀部时的正确方向是？',
    options: ['从后往前（从肛门向尿道）', '从前往后（从尿道向肛门）', '随意方向', '用湿巾来回擦'],
    correctAnswer: '从前往后（从尿道向肛门）',
    explanation: '给女婴清洁时必须从前往后擦（尿道→肛门方向），防止肛区细菌被带入尿道和阴道，降低尿路感染风险。男婴也应避免把肛门区域细菌带到阴茎尿道口。',
  },
  {
    id: 'q-c3-7',
    levelId: 'newborn-care-3',
    type: 'truefalse',
    text: '新生儿出生后头几天排出的大便是黑色粘稠的（胎便），这是正常现象。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '新生儿最初排出的大便称为胎便——黑色或墨绿色、粘稠如焦油状，由羊水、黏液、皮肤细胞等在宫内积聚而成。通常在出生24-48小时内排出。之后随着喂养开始，大便颜色慢慢变为绿褐过渡色，最终变成母乳宝宝的芥末黄软便或配方奶宝宝偏暗的糊状便。',
  },
  {
    id: 'q-c3-8',
    levelId: 'newborn-care-3',
    type: 'scenario',
    text: '宝宝出生第20天，配方奶喂养。你注意到宝宝已经有超过2天没有排大便，但精神好、吃奶正常、肚子不胀、每天有足够的湿尿布、排气正常。你该怎么做？',
    options: [
      '立即用开塞露给宝宝通便',
      '配方奶宝宝也可能偶尔2-3天不排便，只要精神好、吃奶正常、无腹胀不适，可先观察；若超过3天或伴随明显哭闹腹胀，需咨询医生',
      '这很危险，马上去急诊',
      '给宝宝多喂水',
    ],
    correctAnswer: '配方奶宝宝也可能偶尔2-3天不排便，只要精神好、吃奶正常、无腹胀不适，可先观察；若超过3天或伴随明显哭闹腹胀，需咨询医生',
    explanation: '配方奶喂养的宝宝通常每天排便 1-3 次，但也可能偶尔隔 1-2 天不排便。如果超过 3 天不排便或伴随明显腹胀、哭闹、拉不出，应咨询儿科医生。不要自行用开塞露——需先排除器质性原因（如先天性巨结肠）。',
  },
  {
    id: 'q-c3-9',
    levelId: 'newborn-care-3',
    type: 'multiple',
    text: '如何区分普通尿布疹和需要就医的酵母菌感染？',
    options: [
      '普通尿布疹通常是均匀发红的平面区域',
      '酵母菌感染通常具有边界清晰的"卫星灶"小脓疱，皮肤褶皱处受累更重',
      '普通护臀膏涂2-3天后没有改善反而加重，可能提示酵母菌感染',
      '完全无法区分，任何红疹都要去医院',
    ],
    correctAnswer: ['普通尿布疹通常是均匀发红的平面区域', '酵母菌感染通常具有边界清晰的"卫星灶"小脓疱，皮肤褶皱处受累更重', '普通护臀膏涂2-3天后没有改善反而加重，可能提示酵母菌感染'],
    explanation: '区分标准：普通尿布疹——均匀发红、表面较平、不累及皮肤褶皱深处；酵母菌感染——鲜红发亮的斑片、边缘有"卫星状"小脓疱和小红点、皮肤褶皱深处也受影响、普通护臀膏无效。酵母菌感染需医生开具抗真菌药膏。',
  },
  {
    id: 'q-c3-10',
    levelId: 'newborn-care-3',
    type: 'single',
    text: '新生儿大便中以下哪种情况属于需要警惕的异常？',
    options: [
      '母乳宝宝的大便呈芥末黄色带细小颗粒',
      '大便呈陶土色（灰白色）',
      '出生第3天排出绿褐色过渡便',
      '大便偶尔有少量黏液丝',
    ],
    correctAnswer: '大便呈陶土色（灰白色）',
    explanation: '陶土色（灰白色）大便可能提示胆道闭锁或胆道问题——胆红素无法正常排入肠道。这是需要紧急就医的重要信号。母乳宝宝的芥末黄软便、出生初期的绿褐过渡便都是正常的；偶尔少量黏液多与口腔分泌物吞咽有关，如果持续大量黏液便才需关注。',
  },
  {
    id: 'q-c3-11',
    levelId: 'newborn-care-3',
    type: 'truefalse',
    text: '使用婴儿爽身粉可以有效预防和治疗尿布疹。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '不推荐使用爽身粉。玉米淀粉类粉末可能滋生酵母菌真菌；滑石粉类被吸入肺部可导致呼吸道问题。预防和轻度尿布疹应使用含氧化锌的护臀膏形成隔离保护层。',
  },
  {
    id: 'q-c3-12',
    levelId: 'newborn-care-3',
    type: 'scenario',
    text: '宝宝每次换尿布时都大哭，尤其是清洁肛门区域时哭得更厉害。你发现肛门周围有一圈发红的皮肤，看起来像擦伤。你首先会尝试什么？',
    options: [
      '改用无香料、无酒精的婴儿湿巾或温水+柔软棉布清洁，每次换尿布后涂隔离护臀膏，减少擦拭力度',
      '停止清洁，让宝宝自己愈合',
      '每次清洁后涂酒精消毒',
      '不用管，宝宝的皮肤自愈能力很强',
    ],
    correctAnswer: '改用无香料、无酒精的婴儿湿巾或温水+柔软棉布清洁，每次换尿布后涂隔离护臀膏，减少擦拭力度',
    explanation: '肛门周围发红常见的刺激源是湿巾中的酒精、香料成分或过度擦拭摩擦。改用温水+棉布（或确认无香精无酒精的湿巾）、轻轻蘸拭而非来回擦、每次换后涂含氧化锌的护臀膏形成保护屏障，2-3天通常能明显改善。',
  },

  // ==========================================
  // 1-3 个月 - 喂养模块 - 关卡1：喂养节奏的变化
  // ==========================================
  {
    id: 'q-s1f1-1',
    levelId: 'stage1-feeding-1',
    type: 'single',
    text: '满月后宝宝的胃容量和喂养间隔会发生什么变化？',
    options: ['胃容量变小，需要更频繁喂养', '胃容量增大，单次吃得更多，间隔可从2-3小时拉长到3-4小时', '喂养间隔应该强制控制在4小时以上', '和新生儿期完全一样'],
    correctAnswer: '胃容量增大，单次吃得更多，间隔可从2-3小时拉长到3-4小时',
    explanation: '满月后胃容量明显增大，单次吃奶量增加，喂奶间隔自然拉长。但不应用固定时间表约束宝宝，观察饥饿信号比看钟表更科学。',
  },
  {
    id: 'q-s1f1-2',
    levelId: 'stage1-feeding-1',
    type: 'truefalse',
    text: '宝宝满月后夜间连续睡眠时间逐步延长是神经系统成熟的标志。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '新生儿期夜间需频繁吃奶，满月后胃容量增大伴随神经系统成熟，夜间连续睡眠时间逐渐拉长。2个月时很多宝宝可连续睡5-6小时。',
  },
  {
    id: 'q-s1f1-3',
    levelId: 'stage1-feeding-1',
    type: 'multiple',
    text: '1-3个月宝宝夜间喂养的合理做法包括哪些？',
    options: ['观察宝宝的自然睡眠节奏，不一定定点叫醒喂奶', '只要宝宝精神好、体重正常增长，夜间减少到1-2次是正常的', '宝宝可以连续睡8小时了也必须叫醒喂奶', '配方奶消化较慢，满月后夜间通常只需喂1-2次，部分宝宝可连续睡5-6小时'],
    correctAnswer: ['观察宝宝的自然睡眠节奏，不一定定点叫醒喂奶', '只要宝宝精神好、体重正常增长，夜间减少到1-2次是正常的', '配方奶消化较慢，满月后夜间通常只需喂1-2次，部分宝宝可连续睡5-6小时'],
    explanation: '1-3个月夜间喂奶次数自然减少是正常进程。配方奶消化慢（约3-4小时），满月后很多宝宝夜间只需喂1-2次，2个月时可连续睡5-6小时。关键是白天精神好、体重正常增长。',
  },
  {
    id: 'q-s1f1-4',
    levelId: 'stage1-feeding-1',
    type: 'scenario',
    text: '宝宝7周大，近3天表现得特别"贪吃"——每次喝完后不到2小时又哭着要吃，奶量从每顿 90ml 涨到 120ml 还不够，比平时烦躁。家人担心"是不是没吃饱"。你该怎么判断？',
    options: [
      '明显没吃饱，每顿再多喂 30ml',
      '这很可能是第3-6周的猛长期，宝宝需要暂时增加单次喂养量，坚持按需喂养2-3天通常会自行恢复',
      '宝宝生病了，马上送医院',
      '减少喂养量让宝宝"饿一饿"',
    ],
    correctAnswer: '这很可能是第3-6周的猛长期，宝宝需要暂时增加单次喂养量，坚持按需喂养2-3天通常会自行恢复',
    explanation: '猛长期通常在第3-6周出现。配方奶喂养的宝宝在猛长期可能需要每次多加 10-20ml，按需喂养 2-3 天后通常会自行恢复稳定。但不应无限制加量导致过度喂养——吃饱后宝宝会自己停下来，尊重宝宝的饱腹信号。',
  },
  {
    id: 'q-s1f1-5',
    levelId: 'stage1-feeding-1',
    type: 'truefalse',
    text: '猛长期宝宝频繁索奶说明喂养不足，每次都应当尽量多喂一些让宝宝吃够。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '猛长期可以适当增加单次喂养量（配方奶每次多加 10-20ml），但并非"喂越多越好"。过度喂养可能导致吐奶、胀气和不舒服。观察宝宝的饱腹信号——如松开奶嘴、转头、不再吞咽——及时停止。猛长期通常在 2-3 天后自行缓解。',
  },
  {
    id: 'q-s1f1-6',
    levelId: 'stage1-feeding-1',
    type: 'single',
    text: '判断1-3个月宝宝喂养是否足够，最可靠的客观指标是什么？',
    options: ['严格按照时间表喂', '体重沿自己的生长曲线稳定增长 + 每天6-8片湿尿布', '宝宝长得越胖越好', '每次喂完奶后宝宝不哭'],
    correctAnswer: '体重沿自己的生长曲线稳定增长 + 每天6-8片湿尿布',
    explanation: '体重持续增长和充足的尿量是判断奶量摄入是否充足的最客观指标。不应用"胖"作为唯一标准，也不用时间表绑架喂养。',
  },
  {
    id: 'q-s1f1-7',
    levelId: 'stage1-feeding-1',
    type: 'multiple',
    text: '以下哪些是猛长期宝宝的典型表现？',
    options: ['每1-2小时就想吃一次奶', '伴随烦躁和睡眠不稳', '体重不增反降', '坚持按需喂养2-3天后自行恢复'],
    correctAnswer: ['每1-2小时就想吃一次奶', '伴随烦躁和睡眠不稳', '坚持按需喂养2-3天后自行恢复'],
    explanation: '猛长期表现为频繁索奶、烦躁和睡眠波动。只要坚持按需喂养，2-3天后通常自行恢复。体重应该是增长的，不增反降需要就医。',
  },
  {
    id: 'q-s1f1-8',
    levelId: 'stage1-feeding-1',
    type: 'scenario',
    text: '宝宝2个月，最近从每天晚上起来3次吃奶减少到1次，能连续睡6小时。但妈妈担心"宝宝会不会饿坏了"，准备设闹钟叫醒喂奶。你需要怎么建议？',
    options: [
      '宝宝都2个月了还管什么，设闹钟是正确的',
      '只要宝宝白天精神好、体重增长正常，夜间连续睡6小时是好事，不必叫醒喂奶',
      '2个月宝宝必须每3小时喂一次，必须叫醒',
      '说明宝宝身体有问题，需要马上就医',
    ],
    correctAnswer: '只要宝宝白天精神好、体重增长正常，夜间连续睡6小时是好事，不必叫醒喂奶',
    explanation: '到2个月时，许多宝宝夜间可连续睡5-6小时。只要白天精神好、体重正常增长，这是健康的发育进程，不需要强行叫醒。',
  },

  // ==========================================
  // 1-3 个月 - 喂养模块 - 关卡2：喂养误区与边界
  // ==========================================
  {
    id: 'q-s1f2-1',
    levelId: 'stage1-feeding-2',
    type: 'single',
    text: '1-3个月宝宝的正确喂养方式是？',
    options: ['可以少量添加米汤补充营养', '只需要母乳或配方奶，不能喂任何辅食或果汁', '可以开始每天喂少量果汁补充维生素', '可以吃研磨得很细的米粉'],
    correctAnswer: '只需要母乳或配方奶，不能喂任何辅食或果汁',
    explanation: '6个月之前宝宝只需要母乳或配方奶。过早添加固体食物或果汁可能取代重要营养、增加过敏风险、增加呛咳窒息风险。吐舌反射在4个月前非常强大。',
  },
  {
    id: 'q-s1f2-2',
    levelId: 'stage1-feeding-2',
    type: 'truefalse',
    text: '老人说"喝点米汤长得壮"，给2个月宝宝喂米汤是可以的。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '1-3个月宝宝的消化系统和免疫功能还没准备好接受母乳/配方奶以外的任何食物。米汤营养密度低，会占据胃容量取代真正需要的母乳/配方奶。',
  },
  {
    id: 'q-s1f2-3',
    levelId: 'stage1-feeding-2',
    type: 'multiple',
    text: '以下哪些是1-3个月宝宝不应接触的食物/饮品？',
    options: ['果汁', '米汤或米粉', '蜂蜜', '母乳'],
    correctAnswer: ['果汁', '米汤或米粉', '蜂蜜'],
    explanation: '果汁、米汤米粉等辅食都应等到6个月。蜂蜜尤其危险——1岁以下婴儿食用可能感染肉毒杆菌导致严重疾病。',
  },
  {
    id: 'q-s1f2-4',
    levelId: 'stage1-feeding-2',
    type: 'scenario',
    text: '宝宝刚吃完奶20分钟又开始哭了，家人说"肯定没吃饱"准备再喂。你观察发现宝宝尿布是干的、体温正常，刚吃完10分钟后拍出了一个大气嗝。此时比较合适的做法是？',
    options: [
      '立刻再喂一次奶',
      '先尝试其他安抚方式，如抱起来轻拍、排查胀气或安抚需求，而不是立即再次喂奶',
      '让宝宝自己哭一会儿就好了',
      '判断宝宝一定是生病了，马上去医院',
    ],
    correctAnswer: '先尝试其他安抚方式，如抱起来轻拍、排查胀气或安抚需求，而不是立即再次喂奶',
    explanation: '刚刚喂完的宝宝哭泣不一定因为饿。应先排查尿布、胀气、安抚需求等其他原因。频繁用喂奶作为唯一回应会让宝宝混淆"吃奶"和"求安抚"，也容易导致过度喂养。',
  },
  {
    id: 'q-s1f2-5',
    levelId: 'stage1-feeding-2',
    type: 'truefalse',
    text: '看到2个月宝宝"肉嘟嘟"的脸颊就说明过度喂养了，应该减少奶量。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '婴儿期需要高脂肪饮食支持大脑和神经系统发育，"婴儿肥"不等于肥胖。绝大多数"婴儿肥"会在宝宝开始爬行和走路后自然消失。关键是看生长趋势而非外观。',
  },
  {
    id: 'q-s1f2-6',
    levelId: 'stage1-feeding-2',
    type: 'single',
    text: '关于6个月以下婴儿喝果汁的说法，正确的是？',
    options: ['每天可以少量喝稀释的苹果汁', '绝对不能喝任何果汁', '喝果汁比喝配方奶更有营养', '可以喝但不能用奶瓶'],
    correctAnswer: '绝对不能喝任何果汁',
    explanation: 'AAP明确建议6个月以下婴儿不应给予任何果汁。即使到6个月以后加果汁，也应用杯子而非奶瓶，每天不超过120毫升。果汁营养密度远低于母乳或配方奶。',
  },
  {
    id: 'q-s1f2-7',
    levelId: 'stage1-feeding-2',
    type: 'multiple',
    text: '1-3个月宝宝正确喂养的原则包括哪些？',
    options: ['按需喂养，观察饥饿和饱腹信号', '遵循宝宝的饱腹信号——吃饱了会自己停下来', '为了让宝宝长得更大，哄着多喂一些', '不要仅凭外表判断"太胖"或"太瘦"'],
    correctAnswer: ['按需喂养，观察饥饿和饱腹信号', '遵循宝宝的饱腹信号——吃饱了会自己停下来', '不要仅凭外表判断"太胖"或"太瘦"'],
    explanation: '按需喂养、尊重宝宝的饱腹信号、不凭外表判断体态——这些帮助宝宝建立健康的自我调节能力。',
  },
  {
    id: 'q-s1f2-8',
    levelId: 'stage1-feeding-2',
    type: 'scenario',
    text: '2个半月的宝宝，每次喝完奶瓶后还表现出想吃的样子，但再给奶瓶吸几口就烦躁哭闹还推开。家人说"肯定没吃饱"要再冲更多奶。妈妈观察发现宝宝每天有 7-8 片湿尿布、体重增长正常。你判断更可能是什么？',
    options: [
      '确实是没吃饱，每顿加 30ml',
      '宝宝可能只是想安抚性吮吸而并非真饿，尝试用安抚奶嘴或其他安抚方式',
      '宝宝严重营养不良，需要马上就医',
      '宝宝对奶粉过敏了',
    ],
    correctAnswer: '宝宝可能只是想安抚性吮吸而并非真饿，尝试用安抚奶嘴或其他安抚方式',
    explanation: '尿量充足+体重正常=奶量够了。宝宝喝完奶后还含着可能是安抚性吮吸需求而非饥饿。此时可以先尝试其他安抚方式，而非立即再加奶——过度喂养会导致吐奶和胀气不适。',
  },

  // ==========================================
  // 1-3 个月 - 喂养模块 - 关卡3：吐奶升级与胃食管反流
  // ==========================================
  {
    id: 'q-s1f3-1',
    levelId: 'stage1-feeding-3',
    type: 'truefalse',
    text: '宝宝满月后还吐奶就是不正常的，需要立即就医。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '生理性溢奶在3个月内仍然非常常见，因为贲门肌肉尚未发育成熟（通常需到4-6个月才能发育强壮）。只要量少、宝宝精神好、体重正常增长，就属于正常。',
  },
  {
    id: 'q-s1f3-2',
    levelId: 'stage1-feeding-3',
    type: 'single',
    text: '贲门（胃与食道连接处）肌肉通常到什么月龄才能发育强壮？',
    options: ['满月', '约1-2个月', '约4-6个月', '约12个月'],
    correctAnswer: '约4-6个月',
    explanation: '贲门是一圈环形肌肉，在出生时还很松弛。通常到4-6个月发育强壮后，溢奶会自然减少。',
  },
  {
    id: 'q-s1f3-3',
    levelId: 'stage1-feeding-3',
    type: 'multiple',
    text: '以下哪些信号提示可能不是普通溢奶，而是需要就医的胃食管反流病（GERD）？',
    options: ['每次喂养后都大量喷射状呕吐', '喂养过程中宝宝弓背、扭动、哭闹', '宝宝拒绝吃奶', '体重增长不良'],
    correctAnswer: ['每次喂养后都大量喷射状呕吐', '喂养过程中宝宝弓背、扭动、哭闹', '宝宝拒绝吃奶', '体重增长不良'],
    explanation: 'GERD的关键信号：喷射状大量呕吐、喂养时弓背哭闹（胃酸灼伤食道疼痛）、拒食（吃奶=疼的条件反射）、体重增长不良。任何组合都应咨询医生。',
  },
  {
    id: 'q-s1f3-4',
    levelId: 'stage1-feeding-3',
    type: 'scenario',
    text: '宝宝10周大，最近每次喂完奶都会弓着背扭动身体、大声哭闹，有时候刚吃几口就哭着推开奶瓶，吐奶的量也比以前大了。体重近两周几乎没有增长。你建议该怎么做？',
    options: [
      '"吐奶每个宝宝都有，大点就好了"——继续观察',
      '立即自行换一种防吐奶奶粉',
      '这些症状（弓背哭闹+拒食+体重不增）提示可能GERD，应带宝宝看儿科医生',
      '给宝宝吃消化药',
    ],
    correctAnswer: '这些症状（弓背哭闹+拒食+体重不增）提示可能GERD，应带宝宝看儿科医生',
    explanation: '弓背哭闹+拒食+体重增长不良是GERD的典型三联征，需要医生评估。不要自行换奶粉或用药。',
  },
  {
    id: 'q-s1f3-5',
    levelId: 'stage1-feeding-3',
    type: 'single',
    text: '减少吐奶的有效方法不包括以下哪一项？',
    options: ['少量多次喂养', '喂奶中拍嗝1-2次', '喂完奶后立即让宝宝平躺', '喂后竖抱或保持半坐姿势20-30分钟'],
    correctAnswer: '喂完奶后立即让宝宝平躺',
    explanation: '正确的做法是喂完奶后竖抱或保持半坐20-30分钟，利用重力让奶留在胃中。立即平躺会增加溢奶概率。',
  },
  {
    id: 'q-s1f3-6',
    levelId: 'stage1-feeding-3',
    type: 'truefalse',
    text: '喂奶后应该立即给宝宝换尿布或做俯趴练习，帮助消化。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '喂奶后应避免立即换尿布、俯趴或剧烈摇晃宝宝，这些动作可能挤压腹部增加吐奶。应先竖抱或保持半坐20-30分钟。',
  },
  {
    id: 'q-s1f3-7',
    levelId: 'stage1-feeding-3',
    type: 'multiple',
    text: '以下哪些是减少宝宝吐奶的有效方法？',
    options: ['少量多次喂养，避免胃被撑得太大', '喂奶过程中途拍嗝1-2次', '喂后竖抱或保持半坐姿势20-30分钟', '床垫下垫小角度，床头略高于床尾'],
    correctAnswer: ['少量多次喂养，避免胃被撑得太大', '喂奶过程中途拍嗝1-2次', '喂后竖抱或保持半坐姿势20-30分钟', '床垫下垫小角度，床头略高于床尾'],
    explanation: '以上四种方法都是经过验证有效的减少吐奶技巧。注意床垫垫高应垫在床垫下而非枕头上，保证宝宝仍仰卧在平坦硬床面上。',
  },
  {
    id: 'q-s1f3-8',
    levelId: 'stage1-feeding-3',
    type: 'scenario',
    text: '宝宝每次吃完奶嘴角流出一两口奶，精神很好，胖嘟嘟的，体重增长正常。家人很紧张，每次喂完都竖抱40分钟以上不敢放。你怎么看？',
    options: [
      '这是严重问题，需要进一步检查',
      '这是正常的生理性溢奶，竖抱20-30分钟足够，不必过度焦虑',
      '立即换用防吐奶特殊配方奶粉',
      '每次喂完让宝宝平躺不动',
    ],
    correctAnswer: '这是正常的生理性溢奶，竖抱20-30分钟足够，不必过度焦虑',
    explanation: '少量溢奶+精神好+体重正常=生理性溢奶。竖抱20-30分钟足够了，过度竖抱会增加照护者负担。大多数宝宝4-6个月后溢奶会自然减少。',
  },

  // ==========================================
  // 1-3 个月 - 睡眠模块 - 关卡1：睡眠规律的形成
  // ==========================================
  {
    id: 'q-s1s1-1',
    levelId: 'stage1-sleep-1',
    type: 'single',
    text: '宝宝通常什么时候开始建立昼夜节律？',
    options: ['出生第一天', '约6-8周（2个月左右）', '4个月以后', '6个月以后'],
    correctAnswer: '约6-8周（2个月左右）',
    explanation: '宝宝到6-8周时松果体开始分泌褪黑素，逐步建立起昼夜节律。你会注意到夜间连续睡眠时间变长。',
  },
  {
    id: 'q-s1s1-2',
    levelId: 'stage1-sleep-1',
    type: 'truefalse',
    text: '帮助宝宝建立昼夜节律的正确做法是白天保持房间明亮有正常声音，夜间喂奶时保持灯光昏暗不说话。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '白天明亮+声音、夜间昏暗+安静，这套"昼夜信号"能有效促进宝宝节律的建立。',
  },
  {
    id: 'q-s1s1-3',
    levelId: 'stage1-sleep-1',
    type: 'multiple',
    text: '1-3个月宝宝的小睡规律特点包括哪些？',
    options: ['白天通常需要3-4次小睡', '小睡时长还不稳定，30分钟到2小时不等', '醒1-2小时后通常需要小睡', '小睡规律已经定型，每天固定时间入睡'],
    correctAnswer: ['白天通常需要3-4次小睡', '小睡时长还不稳定，30分钟到2小时不等', '醒1-2小时后通常需要小睡'],
    explanation: '1-3个月小睡规律尚未定型，时长和节奏不稳定。关键是观察疲倦信号而非死板卡时间。',
  },
  {
    id: 'q-s1s1-4',
    levelId: 'stage1-sleep-1',
    type: 'scenario',
    text: '宝宝快2个月了，白天有几次只睡了30分钟就醒了，家人认为"睡太少不行"想一直抱着不让醒。宝宝的疲倦信号是揉眼睛和打哈欠，但哄了20分钟还不睡。你建议？',
    options: [
      '必须哄到睡着为止，即使抱1小时',
      '先试着安抚20分钟，如果仍然清醒，就让宝宝在地垫上玩一会儿，等待下一个睡眠窗口',
      '放弃小睡，等晚上再睡',
      '一定是身体有问题，马上去医院',
    ],
    correctAnswer: '先试着安抚20分钟，如果仍然清醒，就让宝宝在地垫上玩一会儿，等待下一个睡眠窗口',
    explanation: '如果哄了20分钟还不睡，可能宝宝暂时不需要睡或已过了睡眠窗口。强制哄睡会让双方都挫败。休息一会儿再试，或等待下一个疲倦信号。',
  },
  {
    id: 'q-s1s1-5',
    levelId: 'stage1-sleep-1',
    type: 'single',
    text: '宝宝疲倦的早期信号不包括以下哪一项？',
    options: ['打哈欠', '揉眼睛', '眼神发直呆滞', '兴奋手舞足蹈大声笑'],
    correctAnswer: '兴奋手舞足蹈大声笑',
    explanation: '打哈欠、揉眼睛、眼神呆滞、对互动失去兴趣都是疲倦信号。而过度兴奋的大笑和舞动可能已经是"疲劳亢奋"的晚期信号——此时反而更难入睡。',
  },
  {
    id: 'q-s1s1-6',
    levelId: 'stage1-sleep-1',
    type: 'truefalse',
    text: '对1-2个月宝宝采取"让他哭一会儿就会自己睡"的哭声免疫法是合适的。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '入睡能力随着月龄增长逐渐发展。对1-2个月宝宝采取哭声免疫法既不适合其发育水平，也可能影响安全依附关系建立。',
  },
  {
    id: 'q-s1s1-7',
    levelId: 'stage1-sleep-1',
    type: 'scenario',
    text: '宝宝8周大，最近夜里连续睡了6个小时没有要求吃奶。妈妈醒来发现后很担心，觉得"应该叫醒喂"。宝宝白天精神好、吃奶有力、体重增长正常。你怎么看？',
    options: [
      '应该坚持设闹钟每3小时叫醒喂奶',
      '夜间连续睡眠延长是昼夜节律建立的正常标志，只要白天表现好就不用叫醒',
      '说明宝宝生病了嗜睡',
      '说明白天喂太多了',
    ],
    correctAnswer: '夜间连续睡眠延长是昼夜节律建立的正常标志，只要白天表现好就不用叫醒',
    explanation: '2个月左右的宝宝夜间连续睡5-6小时是健康的发育标志，说明昼夜节律正在建立。只要白天精神好、体重正常，无需叫醒。',
  },

  // ==========================================
  // 1-3 个月 - 睡眠模块 - 关卡2：SIDS预防升级
  // ==========================================
  {
    id: 'q-s1s2-1',
    levelId: 'stage1-sleep-2',
    type: 'single',
    text: '婴儿猝死综合征（SIDS）最高发的年龄段是？',
    options: ['出生1周内', '2-4个月', '6-8个月', '10-12个月'],
    correctAnswer: '2-4个月',
    explanation: '很多家长以为过了新生儿期SIDS风险就降低了，但实际上2-4个月正是高峰。这个阶段仍然必须严格执行安全睡眠规则。',
  },
  {
    id: 'q-s1s2-2',
    levelId: 'stage1-sleep-2',
    type: 'multiple',
    text: '1-3个月阶段预防SIDS的核心措施包括哪些？',
    options: ['始终仰卧入睡', '使用硬质婴儿床垫，铺紧床单', '床上没有任何枕头、毯子、毛绒玩具', '宝宝睡在父母卧室的独立小床上（同室不同床）'],
    correctAnswer: ['始终仰卧入睡', '使用硬质婴儿床垫，铺紧床单', '床上没有任何枕头、毯子、毛绒玩具', '宝宝睡在父母卧室的独立小床上（同室不同床）'],
    explanation: '这四项是AAP最核心的SIDS预防建议。同室不同床至少到6个月最好到1岁，所有松软物品都不应出现在婴儿床上。',
  },
  {
    id: 'q-s1s2-3',
    levelId: 'stage1-sleep-2',
    type: 'scenario',
    text: '宝宝2个半月，最近在仰卧时开始把身体往一侧扭转，腿也踢得很高。家人说"已经开始想翻身了"，但仍然用襁褓裹着睡觉，因为"裹着睡得香"。你该怎么建议？',
    options: [
      '裹着睡得好就继续裹，不会有事',
      '一旦宝宝出现任何翻身迹象，襁褓必须立即停止——裹着翻到俯卧无法自己翻回有窒息风险',
      '可以把襁褓换成更紧的',
      '允许继续裹着但把宝宝放在大人床上看着',
    ],
    correctAnswer: '一旦宝宝出现任何翻身迹象，襁褓必须立即停止——裹着翻到俯卧无法自己翻回有窒息风险',
    explanation: '出现任何翻身迹象都必须停止襁褓。裹住的宝宝一旦意外翻成俯卧，没有"解锁"手臂的能力，有极高窒息风险。可换用睡袋作为替代。',
  },
  {
    id: 'q-s1s2-4',
    levelId: 'stage1-sleep-2',
    type: 'truefalse',
    text: '襁褓只要裹得舒服就一直可以给宝宝用。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '宝宝出现翻身迹象（约2-3个月）时必须立即停止使用襁褓。过紧的襁褓还可能影响髋关节发育。',
  },
  {
    id: 'q-s1s2-5',
    levelId: 'stage1-sleep-2',
    type: 'single',
    text: '关于安抚奶嘴的使用，以下哪项是正确的？',
    options: ['安抚奶嘴会增加SIDS风险，不应该使用', '入睡时使用安抚奶嘴可降低SIDS风险', '应该用绳子把奶嘴系在宝宝脖子上', '强迫宝宝接受安抚奶嘴'],
    correctAnswer: '入睡时使用安抚奶嘴可降低SIDS风险',
    explanation: '多项研究证明入睡时使用安抚奶嘴可显著降低SIDS发生率。建议等宝宝喂养规律基本稳定（约3-4周）后再引入。不要用绳子系、不要强迫。',
  },
  {
    id: 'q-s1s2-6',
    levelId: 'stage1-sleep-2',
    type: 'scenario',
    text: '宝宝3个月，喂养规律已稳定。入睡时可以给安抚奶嘴吗？如果睡着后奶嘴掉了要重新塞回去吗？',
    options: [
      '不能给，安抚奶嘴对宝宝不好',
      '可以给，如果睡着后掉了不需要重新塞回去',
      '可以给，掉了要马上塞回去',
      '可以给，但要用绳子挂在脖子上防止掉落',
    ],
    correctAnswer: '可以给，如果睡着后掉了不需要重新塞回去',
    explanation: '入睡时给安抚奶嘴可降低SIDS风险。如果睡着后掉了，不需要重新塞回去。绝对不能用绳子系在脖子或衣服上。',
  },
  {
    id: 'q-s1s2-7',
    levelId: 'stage1-sleep-2',
    type: 'truefalse',
    text: '宝宝已经2个月了，SIDS风险已经过去了，可以在婴儿床上放枕头和毛绒玩具。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: 'SIDS高发期恰恰在2-4个月。婴儿床上任何时候都不应放枕头、毯子、毛绒玩具或床围，这些松软物品可能遮挡口鼻造成窒息。',
  },

  // ==========================================
  // 1-3 个月 - 睡眠模块 - 关卡3：睡眠倒退与应对
  // ==========================================
  {
    id: 'q-s1s3-1',
    levelId: 'stage1-sleep-3',
    type: 'single',
    text: '3个月左右宝宝出现"睡眠倒退"——从能睡5-6小时变成频繁夜醒——这说明了什么？',
    options: ['宝宝身体生病了', '你做错了什么导致宝宝睡眠变差', '宝宝的睡眠结构正从婴儿型向成人型转化，是大脑成熟的表现', '说明必须开始睡眠训练了'],
    correctAnswer: '宝宝的睡眠结构正从婴儿型向成人型转化，是大脑成熟的表现',
    explanation: '约3个月时宝宝睡眠结构从REM主导转为NREM主导，更接近成人模式。在转化期宝宝更容易在周期转换时醒来，这是神经系统成熟的标志而非退步。',
  },
  {
    id: 'q-s1s3-2',
    levelId: 'stage1-sleep-3',
    type: 'truefalse',
    text: '3个月睡眠倒退通常为永久性变化，不太可能自己好转。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '睡眠倒退是暂时性的发育阶段，通常在2-4周后自然好转。保持一致性是关键。',
  },
  {
    id: 'q-s1s3-3',
    levelId: 'stage1-sleep-3',
    type: 'multiple',
    text: '有效的睡前程序包括哪些要素？',
    options: ['简单一致的步骤顺序', '过程约20-30分钟', '在昏暗的环境中进行', '宝宝还清醒时放到床上（drowsy but awake）'],
    correctAnswer: ['简单一致的步骤顺序', '过程约20-30分钟', '在昏暗的环境中进行', '宝宝还清醒时放到床上（drowsy but awake）'],
    explanation: '一个好的睡前程序不需要复杂——洗澡→穿睡衣→最后一次喂奶→轻声安抚→放床上。关键是一致性和在宝宝还清醒时放下。',
  },
  {
    id: 'q-s1s3-4',
    levelId: 'stage1-sleep-3',
    type: 'scenario',
    text: '宝宝3个月，最近一周频繁夜醒，以前能睡6小时现在每2小时就醒一次。妈妈很疲惫也很自责，觉得"一定是白天的安排出问题了"。你会怎么建议？',
    options: [
      '马上开始严格的睡眠训练',
      '这很可能是正常的3个月睡眠倒退期，保持一致的睡前程序，夜间醒来先用轻拍安抚而非立即抱起',
      '减少宝宝的小睡时间',
      '睡前多喂一顿加餐',
    ],
    correctAnswer: '这很可能是正常的3个月睡眠倒退期，保持一致的睡前程序，夜间醒来先用轻拍安抚而非立即抱起',
    explanation: '3个月睡眠倒退是暂时的，保持一致的睡前程序、夜间醒来先观察几秒、用轻拍代替抱起，通常在2-4周后好转。',
  },
  {
    id: 'q-s1s3-5',
    levelId: 'stage1-sleep-3',
    type: 'single',
    text: '"放下醒"的主要原因是什么？',
    options: ['宝宝不困', '婴儿的睡眠周期短（约50-60分钟），第一个入睡阶段是浅睡眠，约20分钟后才进入深睡眠', '床不够软', '宝宝饿了'],
    correctAnswer: '婴儿的睡眠周期短（约50-60分钟），第一个入睡阶段是浅睡眠，约20分钟后才进入深睡眠',
    explanation: '婴儿的睡眠周期分浅睡和深睡。浅睡阶段约20分钟，此时被放下很容易醒。等进入深睡眠（手臂完全放松、呼吸深沉平稳）后再放，成功率更高。',
  },
  {
    id: 'q-s1s3-6',
    levelId: 'stage1-sleep-3',
    type: 'multiple',
    text: '应对"放下醒"的实用技巧包括？',
    options: ['等宝宝进入深度睡眠后再放', '放下的动作要慢且稳，屁股先着床再放头', '尝试让宝宝在半睡半醒时躺在小床上入睡（drowsy but awake）', '"让他哭几次就好了"'],
    correctAnswer: ['等宝宝进入深度睡眠后再放', '放下的动作要慢且稳，屁股先着床再放头', '尝试让宝宝在半睡半醒时躺在小床上入睡（drowsy but awake）'],
    explanation: '这些方法利用了婴儿睡眠生理特点来减少"放下醒"。哭声免疫法对这个月龄不推荐。',
  },
  {
    id: 'q-s1s3-7',
    levelId: 'stage1-sleep-3',
    type: 'scenario',
    text: '奶奶认为"宝宝玩累了自然会睡"，所以白天一个劲儿逗宝宝玩，直到宝宝哭闹才让他睡。结果宝宝越来越难哄。问题出在哪里？',
    options: [
      '宝宝太懒了需要更多运动',
      '从"玩累了"才睡可能错过了最佳睡眠窗口——过度疲劳的宝宝反而更难入睡，应观察疲倦信号并及时安排睡眠',
      '说明宝宝根本不困',
      '这是正常的，宝宝就应该这样',
    ],
    correctAnswer: '从"玩累了"才睡可能错过了最佳睡眠窗口——过度疲劳的宝宝反而更难入睡，应观察疲倦信号并及时安排睡眠',
    explanation: '过度疲劳的宝宝体内皮质醇水平升高，反而更难入睡。观察疲倦信号并及时安排睡眠，比等到崩溃更好。',
  },

  // ==========================================
  // 1-3 个月 - 日常护理模块 - 关卡1：皮肤与身体变化
  // ==========================================
  {
    id: 'q-s1c1-1',
    levelId: 'stage1-care-1',
    type: 'single',
    text: '婴儿痤疮通常在什么时间出现？',
    options: ['出生后立即出现', '出生后3-4周', '6个月以后', '1岁左右'],
    correctAnswer: '出生后3-4周',
    explanation: '婴儿痤疮由母体激素通过胎盘传给宝宝引起，通常在出生后3-4周出现，表现为面部红色小丘疹。',
  },
  {
    id: 'q-s1c1-2',
    levelId: 'stage1-care-1',
    type: 'multiple',
    text: '婴儿痤疮的正确处理方法包括？',
    options: ['每天用温水轻轻洗脸', '用干净软毛巾蘸干（不要擦）', '涂抹护肤霜或药膏', '不要挤压'],
    correctAnswer: ['每天用温水轻轻洗脸', '用干净软毛巾蘸干（不要擦）', '不要挤压'],
    explanation: '婴儿痤疮唯一正确的处理是温水洗、蘸干、不涂药、不挤压。通常在几周到2-3个月内自行消退。',
  },
  {
    id: 'q-s1c1-3',
    levelId: 'stage1-care-1',
    type: 'truefalse',
    text: '宝宝脸上长婴儿痤疮，应该涂抹成人祛痘药膏帮助消退。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '婴儿痤疮是由母体激素引起的正常反应，不是感染也不需用药。涂抹任何药膏都可能刺激宝宝娇嫩皮肤。',
  },
  {
    id: 'q-s1c1-4',
    levelId: 'stage1-care-1',
    type: 'single',
    text: '乳痂（脂溢性皮炎）的正确护理方法是？',
    options: ['用指甲或梳子用力刮掉', '洗头前用少量婴儿油软化鳞屑→软毛刷轻按摩→温和洗发水洗掉', '用成人去头屑洗发水猛洗', '不做任何处理让它长'],
    correctAnswer: '洗头前用少量婴儿油软化鳞屑→软毛刷轻按摩→温和洗发水洗掉',
    explanation: '温和三步骤：软化→轻揉→洗掉。不要抠、不要用成人洗发水。绝大多数乳痂在几周到几个月内自愈。',
  },
  {
    id: 'q-s1c1-5',
    levelId: 'stage1-care-1',
    type: 'scenario',
    text: '宝宝头上长了一层黄色的油腻鳞屑，看起来很脏。奶奶说"得用梳子好好刮刮"。你怎么说？',
    options: [
      '奶奶说得对，用细齿梳仔细刮干净',
      '这是正常的乳痂，用指甲抠和梳子刮可能损伤头皮引发感染。应该用婴儿油软化后温和去除',
      '用洗洁精洗最干净',
      '必须用药物才能去除',
    ],
    correctAnswer: '这是正常的乳痂，用指甲抠和梳子刮可能损伤头皮引发感染。应该用婴儿油软化后温和去除',
    explanation: '乳痂与卫生无关，是母体激素刺激皮脂腺所致。用指甲抠或梳子硬刮可能损伤头皮。正确的三步法温和又有效。',
  },
  {
    id: 'q-s1c1-6',
    levelId: 'stage1-care-1',
    type: 'single',
    text: '婴儿湿疹的典型表现是？',
    options: ['红色凸起的脓疱', '面部和四肢伸侧的红色、干燥、粗糙斑块', '白色鳞屑覆盖的斑点', '全身水疱'],
    correctAnswer: '面部和四肢伸侧的红色、干燥、粗糙斑块',
    explanation: '婴儿湿疹表现为红色干燥粗糙斑块，通常出现在面部和四肢伸侧。关键基础护理是保湿——每次洗澡后3分钟内涂抹保湿霜锁水。',
  },
  {
    id: 'q-s1c1-7',
    levelId: 'stage1-care-1',
    type: 'multiple',
    text: '1-3个月宝宝的湿疹基础护理方法包括？',
    options: ['温水短暂洗澡后3分钟内涂保湿霜', '保持室内湿度40%-50%', '避免羊毛等粗糙织物直接接触皮肤', '一出现湿疹就立即换特殊水解奶粉'],
    correctAnswer: ['温水短暂洗澡后3分钟内涂保湿霜', '保持室内湿度40%-50%', '避免羊毛等粗糙织物直接接触皮肤'],
    explanation: '大多数湿疹与皮肤屏障功能不成熟有关（而非食物过敏）。保湿+避免刺激是第一线处理。不应第一反应就盲目换特殊水解奶粉。',
  },

  // ==========================================
  // 1-3 个月 - 日常护理模块 - 关卡2：洗澡与日常卫生升级
  // ==========================================
  {
    id: 'q-s1c2-1',
    levelId: 'stage1-care-2',
    type: 'single',
    text: '1-3个月宝宝的推荐洗澡频率是？',
    options: ['每天洗一次', '每周2-3次', '每周一次', '每两周一次'],
    correctAnswer: '每周2-3次',
    explanation: '1-3个月宝宝活动量有限，每周2-3次足够了。天天洗反而会洗掉保护性油脂导致皮肤干燥。',
  },
  {
    id: 'q-s1c2-2',
    levelId: 'stage1-care-2',
    type: 'truefalse',
    text: '给宝宝每次洗澡都应该用沐浴露彻底清洁。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '不应每次都用沐浴露。过度使用清洁产品会洗掉皮肤表面的保护性油脂，可能导致干燥甚至加重湿疹。清水洗澡经常就足够了。',
  },
  {
    id: 'q-s1c2-3',
    levelId: 'stage1-care-2',
    type: 'single',
    text: '关于男宝宝未环切的包皮护理，正确的是？',
    options: ['每次换尿布时应该把包皮向后翻开清洗', '不要强行回翻包皮——它在婴儿期自然粘连于龟头', '从3个月起每天练习翻开包皮', '用棉签伸入包皮内侧清洗'],
    correctAnswer: '不要强行回翻包皮——它在婴儿期自然粘连于龟头',
    explanation: '婴儿期包皮自然粘连于龟头，强行翻开会造成撕裂和疤痕。包皮自然分离通常在3-5岁时发生。日常清洗从外面清洗即可。',
  },
  {
    id: 'q-s1c2-4',
    levelId: 'stage1-care-2',
    type: 'scenario',
    text: '给宝宝换尿布时，姥姥习惯性地想把男宝的包皮往后推着洗，说"不翻开来洗不干净"。你怎么做？',
    options: [
      '姥姥说得对，帮忙推着洗',
      '温和地解释婴儿期包皮自然粘连不应强行翻开，强行翻开可能造成撕裂和疤痕，外面清洗就够',
      '默不作声，反正平时自己不带',
      '干脆不带男宝去医院做环切',
    ],
    correctAnswer: '温和地解释婴儿期包皮自然粘连不应强行翻开，强行翻开可能造成撕裂和疤痕，外面清洗就够',
    explanation: '男婴期包皮自然粘连于龟头，强行翻开会造成撕裂和疤痕。包皮分离是渐进过程，通常在3-5岁自然完成。日常只需清洗外部。',
  },
  {
    id: 'q-s1c2-5',
    levelId: 'stage1-care-2',
    type: 'single',
    text: '给宝宝剪指甲的最佳时机是？',
    options: ['宝宝醒着玩的时候', '宝宝熟睡时', '洗澡时', '吃饭时'],
    correctAnswer: '宝宝熟睡时',
    explanation: '宝宝熟睡时手臂肌肉完全放松，不会突然抽动，是剪指甲最安全的时机。每周需要剪1-2次。',
  },
  {
    id: 'q-s1c2-6',
    levelId: 'stage1-care-2',
    type: 'multiple',
    text: '给宝宝洗澡的正确做法包括？',
    options: ['水温约37℃（手肘内侧感觉温暖不烫）', '时间控制在5-10分钟', '洗后用软毛巾蘸干', '趁皮肤还微湿时涂保湿霜'],
    correctAnswer: ['水温约37℃（手肘内侧感觉温暖不烫）', '时间控制在5-10分钟', '洗后用软毛巾蘸干', '趁皮肤还微湿时涂保湿霜'],
    explanation: '以上四项是婴儿洗澡关键要点。注意是蘸干不是擦干，减少皮肤摩擦。',
  },
  {
    id: 'q-s1c2-7',
    levelId: 'stage1-care-2',
    type: 'truefalse',
    text: '剪指甲时剪得越圆越好，防止宝宝抓伤自己。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '沿指甲自然弧度剪平即可，不要剪太圆——太圆可能导致指甲向内生长。剪完后用手指摸一遍确认没有尖角。',
  },

  // ==========================================
  // 1-3 个月 - 日常护理模块 - 关卡3：外出与日常活动
  // ==========================================
  {
    id: 'q-s1c3-1',
    levelId: 'stage1-care-3',
    type: 'single',
    text: '6个月以下宝宝户外防晒的正确方法是？',
    options: ['大面积涂抹防晒霜', '使用遮阳伞、遮阳棚、长袖连体衣和宽檐帽等物理遮挡', '让宝宝直接晒晒太阳补钙', '涂成人防晒霜'],
    correctAnswer: '使用遮阳伞、遮阳棚、长袖连体衣和宽檐帽等物理遮挡',
    explanation: 'AAP建议6个月以下婴儿不要使用防晒霜（皮肤吸收风险），应用遮阳伞、遮阳棚、长袖衣服和宽檐帽等物理防晒。',
  },
  {
    id: 'q-s1c3-2',
    levelId: 'stage1-care-3',
    type: 'scenario',
    text: '宝宝2个月，妈妈想带宝宝出门散步。夏天上午10点，阳光比较强。你会建议？',
    options: [
      '直接推出去晒太阳，对宝宝好',
      '选择早晨或傍晚阳光不强的时段，使用推车遮阳棚+长袖衣服+宽檐帽全套物理防晒',
      '给宝宝全身涂防晒霜再出门',
      '不能出门，等宝宝半岁再说',
    ],
    correctAnswer: '选择早晨或傍晚阳光不强的时段，使用推车遮阳棚+长袖衣服+宽檐帽全套物理防晒',
    explanation: '满月后可以户外活动，但应避开10点到16点的强日照时段。6个月以下只能用物理方式防晒，不能涂防晒霜。',
  },
  {
    id: 'q-s1c3-3',
    levelId: 'stage1-care-3',
    type: 'truefalse',
    text: '冬天把穿着厚羽绒服的宝宝塞进安全座椅是可以的，衣服可以保暖。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '厚羽绒服会在安全带和宝宝身体之间形成空隙——碰撞时安全带会失效。应先脱掉厚外套、扣好安全带后再盖毯子。',
  },
  {
    id: 'q-s1c3-4',
    levelId: 'stage1-care-3',
    type: 'multiple',
    text: '使用婴儿安全座椅的正确做法包括？',
    options: ['后向式安装，角度在30°-45°', '每次扣紧五点式安全带', '肩带应紧贴身体', '短途或"几分钟"也不能省略安全带'],
    correctAnswer: ['后向式安装，角度在30°-45°', '每次扣紧五点式安全带', '肩带应紧贴身体', '短途或"几分钟"也不能省略安全带'],
    explanation: '每一次乘车都必须正确使用安全座椅，无论路程多短。安全带紧贴身体是最基本的安全要求。',
  },
  {
    id: 'q-s1c3-5',
    levelId: 'stage1-care-3',
    type: 'single',
    text: '1-3个月宝宝趴着玩（tummy time）的推荐量是？',
    options: ['不需要做', '每天2-3次，每次3-5分钟逐步增加，目标到3个月累计1小时/天', '每次至少30分钟', '只在宝宝哭的时候做'],
    correctAnswer: '每天2-3次，每次3-5分钟逐步增加，目标到3个月累计1小时/天',
    explanation: '从出生后就可以开始趴着玩。1-3个月逐步增加，目标是到3个月每天累计约1小时。必须始终有成人监督。',
  },
  {
    id: 'q-s1c3-6',
    levelId: 'stage1-care-3',
    type: 'scenario',
    text: '宝宝每次被放在地垫上趴着就哭，家人心疼每次不到30秒就抱起来，说"等大点自然就好了"。你建议怎么办？',
    options: [
      '那就不要做了，等大点再说',
      '趴着玩是核心发育活动，可以从30秒开始逐步延长，尝试不同方式如趴在照护者胸口、用镜子吸引等',
      '每次必须坚持到哭停为止',
      '不需要趴着玩，抱够就行',
    ],
    correctAnswer: '趴着玩是核心发育活动，可以从30秒开始逐步延长，尝试不同方式如趴在照护者胸口、用镜子吸引等',
    explanation: '趴着玩对颈部力量、预防扁头和后续翻身坐立至关重要。从30秒开始、用有趣的物品吸引、尝试不同姿势来逐步延长。',
  },
  {
    id: 'q-s1c3-7',
    levelId: 'stage1-care-3',
    type: 'truefalse',
    text: '趴着玩时可以把宝宝放在柔软的枕头或沙发垫上，更舒服。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '趴着玩必须在平坦硬实的地垫上进行。柔软的枕头或沙发垫可能塌陷遮挡口鼻造成窒息。始终有成人全程监督。',
  },

  // ==========================================
  // 1-3 个月 - 安全模块 - 关卡1：防跌落新挑战
  // ==========================================
  {
    id: 'q-s1sf1-1',
    levelId: 'stage1-safety-1',
    type: 'single',
    text: '1-3个月宝宝最常见的跌落场景是什么？',
    options: ['从床上自己爬下来', '在高处换尿布或穿衣时突然蹬腿移动导致跌落', '从楼梯上摔下来', '从婴儿车中滑出来'],
    correctAnswer: '在高处换尿布或穿衣时突然蹬腿移动导致跌落',
    explanation: '1-3个月宝宝腿部蹬踹力量明显增大，换尿布台、成人床、沙发等高处的跌落风险极高。铁律是任何时候不离开、一只手始终扶着。',
  },
  {
    id: 'q-s1sf1-2',
    levelId: 'stage1-safety-1',
    type: 'scenario',
    text: '你正在换尿布台上给宝宝换尿布，突然发现湿巾在两步远的柜子上没拿过来。你该怎么办？',
    options: [
      '转身两步拿一下很快的，马上回来',
      '先把宝宝抱到安全地面（如地垫），再去拿湿巾',
      '一手扶着宝宝，一手伸过去够',
      '不拿了，就这么擦',
    ],
    correctAnswer: '先把宝宝抱到安全地面（如地垫），再去拿湿巾',
    explanation: '哪怕只是转身两步，在换尿布台上的这几秒也足以发生跌落。任何时候都是从安全出发——抱起再拿。',
  },
  {
    id: 'q-s1sf1-3',
    levelId: 'stage1-safety-1',
    type: 'truefalse',
    text: '宝宝2个月还不会翻身，放在沙发上小睡片刻不会有问题。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '2-3个月时部分宝宝可能已经开始尝试翻身——第一次翻身往往在你没预料到的时候发生。而且宝宝用腿蹬踹也能移动。永远不要在高处留宝宝无人看管。',
  },
  {
    id: 'q-s1sf1-4',
    levelId: 'stage1-safety-1',
    type: 'multiple',
    text: '抱宝宝走路时防止自己摔倒的注意事项包括？',
    options: ['穿防滑拖鞋或赤脚', '随时清理地面散落物品', '夜间抱宝宝时留小夜灯', '太累时考虑坐着喂而非站着抱喂'],
    correctAnswer: ['穿防滑拖鞋或赤脚', '随时清理地面散落物品', '夜间抱宝宝时留小夜灯', '太累时考虑坐着喂而非站着抱喂'],
    explanation: '照护者自身状态（如疲劳、头晕）也是导致摔倒的重要因素。减少地面障碍物、保证良好视线、量力而行是关键。',
  },
  {
    id: 'q-s1sf1-5',
    levelId: 'stage1-safety-1',
    type: 'truefalse',
    text: '婴儿摇椅可以放在餐桌上，这样方便边吃饭边看着宝宝。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '婴儿摇椅只能放在平坦地面上——放在桌子、床、沙发等高处的跌落风险极高。必须始终使用安全带。',
  },
  {
    id: 'q-s1sf1-6',
    levelId: 'stage1-safety-1',
    type: 'single',
    text: '婴儿摇椅和安全座椅不是照护者离开的替代方案，这句话的意思是？',
    options: ['可以短暂离开，宝宝很安全', '即使宝宝被安全地放在地面上的摇椅里，也不应长时间无人看管', '用安全座椅就可以不管', '这是对产品的夸大警告'],
    correctAnswer: '即使宝宝被安全地放在地面上的摇椅里，也不应长时间无人看管',
    explanation: '没有任何设备可以替代成人监护。即使宝宝在地面上的安全设备中，也可能发生突发情况需要照护者响应。',
  },

  // ==========================================
  // 1-3 个月 - 安全模块 - 关卡2：居家环境安全升级
  // ==========================================
  {
    id: 'q-s1sf2-1',
    levelId: 'stage1-safety-2',
    type: 'single',
    text: 'AAP是否推荐在婴儿床上安装床围？',
    options: ['推荐，防止宝宝撞头', '不推荐任何类型的床围，曾有婴儿因脸贴在床围上窒息而死', '推荐，但要用软质的', '推荐，但要使用透气的'],
    correctAnswer: '不推荐任何类型的床围，曾有婴儿因脸贴在床围上窒息而死',
    explanation: 'AAP已不推荐任何类型的床围（包括所谓的"透气型"）——曾有窒息致死的报告。床上只保留紧裹的床单和宝宝本人。',
  },
  {
    id: 'q-s1sf2-2',
    levelId: 'stage1-safety-2',
    type: 'multiple',
    text: '检查婴儿床安全性的要点包括？',
    options: ['床栏间距不超过6厘米', '床垫是硬的且紧密贴合床边', '不安装床围', '床上没有枕头、被子、毛绒玩具'],
    correctAnswer: ['床栏间距不超过6厘米', '床垫是硬的且紧密贴合床边', '不安装床围', '床上没有枕头、被子、毛绒玩具'],
    explanation: '婴儿床安全检查涵盖间距、床垫贴合度、无床围和无松软物品。二手婴儿床要特别注意是否满足当前安全标准。',
  },
  {
    id: 'q-s1sf2-3',
    levelId: 'stage1-safety-2',
    type: 'scenario',
    text: '宝宝2个月，家里养了一条温顺的金毛犬。家人在宝宝醒着放在游戏垫上时，离开房间去泡奶，觉得"狗狗从来不伤人"。你怎么看？',
    options: [
      '没事，金毛很温顺的',
      '永远不要让宠物和宝宝单独在一个房间——任何动物都可能被哭声惊扰产生不可预测的反应',
      '可以把宠物和宝宝关在一起培养感情',
      '只有大型犬才需要小心',
    ],
    correctAnswer: '永远不要让宠物和宝宝单独在一个房间——任何动物都可能被哭声惊扰产生不可预测的反应',
    explanation: '即使是最温顺的宠物，也不应和宝宝单独在同一个空间。1-3个月宝宝对宠物毫无防御能力，猫咪甚至可能因温暖趴到身上造成窒息。',
  },
  {
    id: 'q-s1sf2-4',
    levelId: 'stage1-safety-2',
    type: 'truefalse',
    text: '去阳台或室外抽完烟再进来抱宝宝就没事了。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '这不只涉及二手烟问题——三手烟中的尼古丁和致癌物会在吸烟者的衣服、头发和皮肤上残留数小时甚至数天。抱宝宝前应换外套、洗手洗脸。',
  },
  {
    id: 'q-s1sf2-5',
    levelId: 'stage1-safety-2',
    type: 'single',
    text: '二手烟暴露会使SIDS风险增加多少？',
    options: ['不会增加', '增加约10%', '增加约2-3倍', '增加约10倍'],
    correctAnswer: '增加约2-3倍',
    explanation: '烟草暴露是SIDS的可预防危险因素之一，可使SIDS风险增加2-3倍。三手烟同样有害。',
  },
  {
    id: 'q-s1sf2-6',
    levelId: 'stage1-safety-2',
    type: 'truefalse',
    text: '在宝宝房间有人抽烟时开窗通风就可以消除二手烟危害。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '即使开窗，烟雾中的有害物质仍会残留在室内空气中。健康的底线是任何宝宝活动的室内空间都绝对不允许吸烟。',
  },

  // ==========================================
  // 1-3 个月 - 安全模块 - 关卡3：窒息与烫伤预防升级
  // ==========================================
  {
    id: 'q-s1sf3-1',
    levelId: 'stage1-safety-3',
    type: 'single',
    text: '2-3个月宝宝出现什么新的能力会带来窒息新风险？',
    options: ['开始走路', '有意识抓握物品并往嘴里送', '会说话了', '会爬了'],
    correctAnswer: '有意识抓握物品并往嘴里送',
    explanation: '约8周起，宝宝开始伸手够东西并抓住，本能反应就是送进嘴里。所有直径小于4厘米的物品（硬币、纽扣电池、药片等）必须远离宝宝可触及范围。',
  },
  {
    id: 'q-s1sf3-2',
    levelId: 'stage1-safety-3',
    type: 'scenario',
    text: '宝宝10周大，躺在游戏垫上玩。妈妈一边抱着宝宝喂奶一边喝热咖啡。忽然宝宝头一撞，咖啡洒了一点在宝宝手臂上。虽然没有烫伤，但要怎么避免？',
    options: [
      '没事，这次没烫伤问题不大',
      '把咖啡换成常温饮料，或者在宝宝不在怀里时喝热饮——永远不要抱着宝宝喝热饮',
      '继续喝但要更小心',
      '用保温杯喝就没事',
    ],
    correctAnswer: '把咖啡换成常温饮料，或者在宝宝不在怀里时喝热饮——永远不要抱着宝宝喝热饮',
    explanation: '抱着宝宝时接触热饮和热食是导致婴儿烫伤的常见场景。婴儿皮肤比成人薄，同样温度的热液可造成更深程度的烫伤。',
  },
  {
    id: 'q-s1sf3-3',
    levelId: 'stage1-safety-3',
    type: 'multiple',
    text: '1-3个月阶段烫伤预防的主要措施包括？',
    options: ['不在抱着宝宝时接触热饮热食', '用热水浴加热奶而不是微波炉', '洗澡先放冷水再加热水', '热水器温度调至49℃（120℉）以下'],
    correctAnswer: ['不在抱着宝宝时接触热饮热食', '用热水浴加热奶而不是微波炉', '洗澡先放冷水再加热水', '热水器温度调至49℃（120℉）以下'],
    explanation: '以上四项都是婴儿烫伤预防的核心措施。微波炉加热奶会形成不均匀"热点"烫伤口腔。',
  },
  {
    id: 'q-s1sf3-4',
    levelId: 'stage1-safety-3',
    type: 'truefalse',
    text: '给宝宝戴长命锁或护身符可以保佑平安，只要好看戴着没事。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '任何挂在宝宝脖子上的东西都构成勒颈危险——宝宝可能被绳子缠住脖子或在婴儿床栏杆上挂住。如果要佩戴，应夹在衣服上而非挂在脖子上。',
  },
  {
    id: 'q-s1sf3-5',
    levelId: 'stage1-safety-3',
    type: 'single',
    text: '以下哪种物品对婴幼儿窒息风险最大？',
    options: ['泡沫球', '纽扣电池——吞下后可能造成食道烧穿', '大块积木', '硅胶勺子'],
    correctAnswer: '纽扣电池——吞下后可能造成食道烧穿',
    explanation: '纽扣电池是极其危险的窒息和食道损伤来源。吞下后电池电流+消化液可2小时内造成食道烧穿。所有含纽扣电池的物品（遥控器、电子玩具）要确保电池仓拧螺丝锁上。',
  },

  // ==========================================
  // 1-3 个月 - 发育模块 - 关卡1：运动发育里程碑
  // ==========================================
  {
    id: 'q-s1d1-1',
    levelId: 'stage1-dev-1',
    type: 'single',
    text: '2个月宝宝俯卧时正常的抬头表现是？',
    options: ['完全不能抬头', '能抬头约45°', '能抬头90°并长时间保持', '能用手支撑坐起来'],
    correctAnswer: '能抬头约45°',
    explanation: '2个月时宝宝俯卧能抬头约45°，头部控制仍有摇晃但已明显有力。3个月时才能抬头90°并稳定。',
  },
  {
    id: 'q-s1d1-2',
    levelId: 'stage1-dev-1',
    type: 'single',
    text: '抬头发育的正确顺序是？',
    options: [
      '1个月仰卧可转头→2个月俯卧抬头45°→3个月俯卧抬头90°',
      '3个月俯卧抬头90°→2个月俯卧抬头45°→1个月仰卧转头',
      '一出生就能抬头90°',
      '2个月就能坐起来',
    ],
    correctAnswer: '1个月仰卧可转头→2个月俯卧抬头45°→3个月俯卧抬头90°',
    explanation: '抬头发育遵循从低到高的自然进程：1个月仰卧转头→2个月俯卧抬头45°→3个月俯卧抬头90°并稳定。从0岁开始趴着玩是最好的促进。',
  },
  {
    id: 'q-s1d1-3',
    levelId: 'stage1-dev-1',
    type: 'truefalse',
    text: '大多数宝宝在4-5个月才能完成完整翻身，但2-3个月时左右扭动、侧翻是翻身的准备信号。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '翻身通常到4-5个月完成，但2-3个月时身体的左右扭动、侧翻倾向是核心力量和协调性发展的准备信号。此时须停止襁褓、不离开高处。',
  },
  {
    id: 'q-s1d1-4',
    levelId: 'stage1-dev-1',
    type: 'scenario',
    text: '宝宝2个月了，发现他开始盯着自己的手看，双臂在面前挥舞，而且有时候会把手往嘴里塞。家人说"手脏不能吃"给他戴防抓手套。你建议？',
    options: [
      '一直戴手套防止吃手和抓脸，干净卫生',
      '这是正常的"发现手"阶段，自由的手和触觉体验对大脑发育非常重要。定期剪指甲防止抓伤即可',
      '给手套上锁不让打开',
      '把手绑在身体两侧',
    ],
    correctAnswer: '这是正常的"发现手"阶段，自由的手和触觉体验对大脑发育非常重要。定期剪指甲防止抓伤即可',
    explanation: '2-3个月"发现双手"是重要的发育里程碑。手部自由探索和触觉刺激对大脑发育至关重要。戴手套剥夺了这些体验。剪好指甲即可。',
  },
  {
    id: 'q-s1d1-5',
    levelId: 'stage1-dev-1',
    type: 'multiple',
    text: '以下哪些是3个月大需要关注的发育警惕信号？',
    options: ['俯卧时头部完全不能抬离床面', '抱起时头始终向后垂', '四肢异常僵硬或异常松软', '到3个月双手仍持续紧握不伸开'],
    correctAnswer: ['俯卧时头部完全不能抬离床面', '抱起时头始终向后垂', '四肢异常僵硬或异常松软', '到3个月双手仍持续紧握不伸开'],
    explanation: '这些信号如果"持续性"出现（每次都如此），需要儿科医生进一步评估。早发现早干预效果最好。偶尔出现不一定有问题。',
  },
  {
    id: 'q-s1d1-6',
    levelId: 'stage1-dev-1',
    type: 'truefalse',
    text: '宝宝2个月了俯卧完全不能抬头说明他"只是懒"，大点自然会。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '虽然个体差异存在，但到3个月俯卧完全不能抬头需要儿科医生评估。不应简单归因为"懒"而延误早期干预的窗口。',
  },

  // ==========================================
  // 1-3 个月 - 发育模块 - 关卡2：感官与社交发育
  // ==========================================
  {
    id: 'q-s1d2-1',
    levelId: 'stage1-dev-2',
    type: 'single',
    text: '关于1个月宝宝的视觉能力，正确的是？',
    options: ['能看到很远的地方', '只能看黑白的', '能看20-30厘米距离，刚好是喂奶时看到妈妈脸的距离', '已经能分辨所有颜色'],
    correctAnswer: '能看20-30厘米距离，刚好是喂奶时看到妈妈脸的距离',
    explanation: '1个月宝宝的聚焦距离恰好是喂奶时看妈妈脸的距离（20-30厘米）。喜欢黑白高对比度图案。',
  },
  {
    id: 'q-s1d2-2',
    levelId: 'stage1-dev-2',
    type: 'truefalse',
    text: '用手机或平板播放儿歌给宝宝听是促进听觉发育的最好方式。',
    options: ['正确', '错误'],
    correctAnswer: '错误',
    explanation: '真人声音才是最好的听觉刺激——真人能根据宝宝的反应调整音量和音调，提供互动和情感交流。电子设备不能替代。',
  },
  {
    id: 'q-s1d2-3',
    levelId: 'stage1-dev-2',
    type: 'single',
    text: '"社交性微笑"通常在什么时候出现？',
    options: ['出生第一天', '约6-8周', '4个月', '6个月'],
    correctAnswer: '约6-8周',
    explanation: '新生儿睡梦中的微笑是生理性反射。真正的"社交性微笑"——有意识地对人笑——在6-8周左右出现，是社会情感发育的重要里程碑。',
  },
  {
    id: 'q-s1d2-4',
    levelId: 'stage1-dev-2',
    type: 'scenario',
    text: '宝宝8周大，第一次朝妈妈露出了明显的有意识微笑。妈妈高兴了一下，然后继续忙自己的事。你觉得这个微笑意味着什么？',
    options: [
      '只是偶然的面部抽动，没什么意义',
      '这是社交性微笑，是社会情感发育的重大里程碑。应该积极回应——和宝宝做"表情对话"：你笑我也笑',
      '说明宝宝饿了',
      '说明宝宝要拉臭臭了',
    ],
    correctAnswer: '这是社交性微笑，是社会情感发育的重大里程碑。应该积极回应——和宝宝做"表情对话"：你笑我也笑',
    explanation: '6-8周的社交性微笑标志着镜像神经元发育和情感连接的建立。积极回应（你笑我也笑、做夸张表情）能促进社会情感发展。',
  },
  {
    id: 'q-s1d2-5',
    levelId: 'stage1-dev-2',
    type: 'single',
    text: '2个月左右宝宝开始发出"啊啊""咕咕"等元音（cooing阶段），照护者最好的回应方式是？',
    options: ['不回应，因为宝宝听不懂', '模仿宝宝的声音回应——建立"对话"轮流', '用复杂的长句子纠正宝宝', '给宝宝放有声书'],
    correctAnswer: '模仿宝宝的声音回应——建立"对话"轮流',
    explanation: '模仿宝宝发出的声音然后等待回应，这种"模仿对话"是语言发展的黄金法则。宝宝会因此更有动力继续发声和探索语音。',
  },
  {
    id: 'q-s1d2-6',
    levelId: 'stage1-dev-2',
    type: 'multiple',
    text: '促进1-3个月宝宝感官发育的方法包括？',
    options: ['黑白卡和缓慢移动的玩具促进视觉追视', '面对面说话唱歌做表情', '多和宝宝进行"模仿式对话"', '给宝宝看手机视频'],
    correctAnswer: ['黑白卡和缓慢移动的玩具促进视觉追视', '面对面说话唱歌做表情', '多和宝宝进行"模仿式对话"'],
    explanation: '免费又高效的方法：黑白卡+追视练习、面对面互动、模仿发声。不需要昂贵的电子产品，你的脸和声音就是最好的早教工具。',
  },
  {
    id: 'q-s1d2-7',
    levelId: 'stage1-dev-2',
    type: 'truefalse',
    text: '宝宝对巨大的声音完全没有反应，到2个月不看人脸不追视移动物，这些都需要做听力或视力筛查。',
    options: ['正确', '错误'],
    correctAnswer: '正确',
    explanation: '如果宝宝对巨大声音完全没有反应（听力问题可能）或到2个月不追视人脸（视力问题可能），应及时咨询医生做听力或视力评估。',
  },
  ...stage2Questions,
  ...stage3Questions,
];